// Connect on the phone — chat with the org's business systems (read-only, org-gated).
// Mirrors the Outlook/desktop ConnectView 1:1 where it matters: same structured answer
// formatting (• / 1. / **bold heading** / → continuation), same connector chips + ⚡
// recipes, same mail-me + Excel actions, same clear-chat confirm, and the SAME house
// sounds (frontdesk plop on send/done + the RPlay Chat thinking loop while it works).
// Voice = the companion's proven tap-dictate (/api/dictate) — the transcript fires
// straight away as a chat turn. All data flows through our own /api/connect proxy
// (cookie session → internal-secret bridge to outlook.rplay.be).
import { toast, showScreen } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { makeDropdown } from "./dropdown";
import { confirmDiscard } from "./guard";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

// ── types (mirror taskpane/src/api/connect.ts) ──
type Recipe = { id: number; title: string; prompt: string };
type Connector = { id: number; type: string; label: string; profile: string; recipes: Recipe[] };
type Meta = { enabled: boolean; org?: string | null; connectors: Connector[]; expertAllowed?: boolean };
type ScanMeeting = { title: string; time?: string | null; company?: string | null; location?: string | null; attendees: { name?: string | null; email?: string | null }[] };
type Item =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string; expert?: boolean } // expert = the deep Ask Expert result (no re-escalate button)
  | { kind: "thinking"; text: string; done: boolean; ms?: number; open: boolean } // Deep-thinking reasoning trace
  | { kind: "status"; connector: string; tool: string; state: string; ms?: number }
  | { kind: "notice"; text: string }
  // `again` = re-run THIS turn on the user's tap. Pane parity: the automatic mail fallback is
  // gone on purpose (Gery 2026-08-20, "automatisch faal-back naar mail DIT MAG NIET"), so a
  // dropped stream must offer the one recovery left instead of leaving the user with nothing.
  | { kind: "error"; text: string; again?: { q: string; mode?: string; sourceRetry?: boolean }; againFired?: boolean }
  // A proposed helpdesk write awaiting Approve (HELPDESK-TICKETING-SPEC §3.3). Mutated in place.
  | { kind: "confirm"; connectorId: number; action: string; summary: string; preview?: Record<string, unknown>; token: string; state: "pending" | "working" | "done" | "cancelled" | "error"; editing?: boolean; resultMsg?: string; error?: string }
  // Helpdesk ticket cards (spec §3.5 UX): tools stream these as artifacts → always cards, never model prose.
  | { kind: "hd_tickets"; team: string | null; tickets: HdTicket[] }
  | { kind: "hd_ticket"; ticket: HdTicket & { description?: string; messages?: { id: number; author: string; direction: string; body: string; at: string }[]; createdAt?: string } }
  // MCP-Apps card (SEP-1865): a vault tool result that carries its own server-rendered view.
  // `key` is a stable per-card id — item INDEXES shift as the thread grows, and the persistent
  // iframe host (see the card-host section below) must survive every re-render untouched.
  | { kind: "mcp_app"; key: string; app: McpApp }
  // Quick-reply chips the agent offered (offer_choices) — tapping one sends its label as the reply.
  // `scan` = the option starts a Meeting/Deep Scan for its subject (an API call, not a message).
  | { kind: "choices"; prompt?: string | null; options: { label: string; info?: string; ask?: string; pinMode?: string; scan?: { subject: string }; handoff?: { mode: string; prompt: string } }[] }
  // Meeting Scan picker (⚡ → Meeting Scan): the day's appointments as chips, from the Vault
  // calendar. Mutated in place (loading → meetings; Tomorrow swaps the day; picked = started).
  | { kind: "scanpick"; when: "today" | "tomorrow"; date?: string; meetings: ScanMeeting[] | null; loading: boolean; error?: string; picked?: string }
  // KB entries that grounded a draft — expandable, with in-place EDIT (PUT /api/faq re-embeds on a question change).
  | { kind: "hd_kb"; hits: { id: number; question: string; answer: string; pct: number; product?: string }[]; query?: string; open?: number | null; edit?: number | null; saving?: boolean; err?: string; adding?: boolean; added?: boolean }
  // M0/M1.1 (BRAIN-AUTO-MODE-SPEC v2 §3.3): post-execution attribution under the answer — what
  // this turn ACTUALLY consulted, plus "Try another source" (append-only re-run, sourceRetry
  // forces a real lookup). `q` = the question for that re-run.
  | { kind: "srcsum"; route: string; attempts: { source: string; tools: number; status: string }[]; q: string; ms?: number; retryOpen?: boolean; retried?: boolean }
  // M1 auto-routing on mobile: the routed-topic chip above the answer; ✕ appends a re-run in
  // General & web (never erases — spec §3.3). `q` = the question for that re-run.
  | { kind: "routed"; mode: string; q: string; corrected?: boolean }
  | { kind: "retried"; mode: string };

let meta: Meta | null = null;
let metaFresh = false; // meta came from a real fetch this session (not just the snapshot)
let items: Item[] = [];
let busy = false;
let activeId: number | null = null;
let recipesOpen = false;
let abortCtrl: AbortController | null = null;
let mailState: "idle" | "sending" | "sent" | "error" = "idle";
let lastC360: unknown = null; // structured Customer-360 payload of the current turn (mailable report)
// Inline [mail:#id] markers in answers → [✉ Mail] buttons (data-prompt = existing tap wiring).
const mailBtns = (html: string): string =>
  html
    // the optional "(#…)"-tail eats the pseudo-markdown-link form ("[file:#25005](#)") the model
    // sometimes writes — otherwise a bare "(#)" litters the answer (MailVault handoff 2026-08-19)
    .replace(/\[mail:#?(\d+)\](?:\((?:#[^)\s]*)?\))?/g, (_m, id) => `<button class="cn-sample hd-mini cn-mailbtn" data-prompt="✉ Mail me the original #${id}">✉ Mail</button>`)
    // [file:#id] parity with the taskpane (ConnectView renders BOTH chips; this file only did
    // mail markers, so document citations arrived buttonless on mobile).
    .replace(/\[file:#?(\d+)\](?:\((?:#[^)\s]*)?\))?/g, (_m, id) =>
      `<button class="cn-sample hd-mini cn-mailbtn" data-prompt="⬇ Export document #${id} to a folder">⬇ Export</button>` +
      ` <button class="cn-sample hd-mini cn-mailbtn" style="margin-left:4px" data-prompt="✉ Mail me document #${id}">✉ Mail</button>`);
// Vault-chip prompts pin themselves to Vault — mirrors ConnectView.VAULT_CHIP_RE (the Auto
// router sent an Export-tap to Docs, a different id namespace; MailVault handoff 2026-08-19).
const VAULT_CHIP_RE = /^(?:⬇ Export document #\d+|✉ Mail me (?:document|the original) #\d+)/;
let xlsState: "idle" | "building" | "done" | "none" | "error" = "idle";
// Chat options (Chat mode only): Deep thinking (GLM-5.2 + streamed reasoning) and Mail me the result
// (fire-and-forget background email). Both reset on a new chat / after each send.
let deepThinking = false;
let mailMeOn = false;
let askExpertArmed = false; // CRM pre-arm: every answer auto-runs the deep pass
// Per-answer Ask Expert steer (the steer changes per question, so it lives on the answer). expertRunningFrom
// = the answer index whose deep run is in flight (drives the "Mail me when ready" affordance there).
let expertOpenIdx: number | null = null;
let expertSteer = "";
let expertRunningFrom: number | null = null;
let scanAsk = false; // Deep Scan: the next composer send is the scan SUBJECT, not a chat turn
let currentTurn: { messages: { role: string; content: string }[]; deepThinking?: boolean; mode?: BrainMode; connectorIds?: number[]; askExpert?: boolean; refine?: string; auto?: boolean; prevRouted?: string | null; sourceRetry?: boolean } | null = null; // for "Mail me when ready" (M1.1: carries the routing flags)
let mailHandoff = false; // aborting to hand off to mail → suppress the "Stopped" notice
// ‼️ The two strings a user reads when a turn does NOT go to plan. Localised where the rest of
// this app is not: they arrive at the worst possible moment, and this archive is Belgian.
// Keep in step with taskpane/src/ui/ConnectView.tsx (STR).
const UI_LANG = (typeof navigator !== "undefined" ? navigator.language || "en" : "en").slice(0, 2).toLowerCase();
const STR: Record<string, { queued: string; tooLong: string }> = {
  nl: {
    queued: "✉ We mailen je het resultaat zodra het klaar is. Je kunt dit venster sluiten — of hieronder meteen je volgende vraag stellen.",
    tooLong: "✉ Dit antwoord wordt te lang om hier te tonen. Ik werk het volledig af op de achtergrond en mail het je zodra het klaar is. Je kunt dit venster sluiten of meteen een nieuwe vraag stellen.",
  },
  fr: {
    queued: "✉ Nous vous enverrons le résultat par e-mail dès qu'il sera prêt. Vous pouvez fermer cette fenêtre — ou poser votre question suivante ci-dessous.",
    tooLong: "✉ Cette réponse devient trop longue pour être affichée ici. Je la termine en arrière-plan et vous l'envoie par e-mail dès qu'elle est prête. Vous pouvez fermer cette fenêtre ou poser une nouvelle question.",
  },
  de: {
    queued: "✉ Wir mailen Ihnen das Ergebnis, sobald es fertig ist. Sie können dieses Fenster schließen — oder unten gleich die nächste Frage stellen.",
    tooLong: "✉ Diese Antwort wird zu lang für die Anzeige hier. Ich stelle sie im Hintergrund fertig und maile sie Ihnen, sobald sie bereit ist. Sie können dieses Fenster schließen oder gleich eine neue Frage stellen.",
  },
  en: {
    queued: "✉ We'll email you the result when it's ready. Close this any time — or ask the next question below.",
    tooLong: "✉ This answer is getting too long to show here. I'm finishing it in the background and will email it to you as soon as it's ready. You can close this window or ask a new question right away.",
  },
};
const T = STR[UI_LANG] || STR.en;
const MAIL_QUEUED = T.queued;


// ── Brain mode hub (§3/§12 — mirrors taskpane/src/lib/brainModes + ConnectView) ──
// EVERY mode is an in-place chat (option B): Chat (open chat, web on) · CRM (Odoo agent) ·
// the 4 own-data scoped chats · FAQ (the "?" knowledge mode). Switching mode clears the thread.
export type BrainMode = "chat" | "crm" | "helpdesk" | "vault" | "contacts" | "tasks" | "receipts" | "minutes" | "docs" | "quotes" | "faq";
const MODULE_MODES = ["contacts", "tasks", "receipts", "minutes", "docs", "quotes", "faq"] as const;
let mode: BrainMode = "chat";
// ── M1 source policy (BRAIN-AUTO-MODE-SPEC v2 §3.1, mirrors ConnectView) ─────────────────────
// Auto is a POLICY over mode "chat": turns go out as chat + auto:true and the server routes
// them. "General & web" = the same mode with the policy explicitly off. Everything else is an
// explicit mode pick via the Auto ▾ selector (the pill row is gone).
let webExplicit = false;
let selOpen = false;
let prevRouted: string | null = null; // routed-topic stickiness (server echo via the routed event)
const autoOn = (): boolean => mode === "chat" && !webExplicit;
// Quotes is an org feature (like the Quotes tile) — the pill shows only when the org enabled it.
// Snapshot paints instantly (no pop-in); probeQuotesOn() revalidates via /api/quotes (403 = off).
let quotesOn = loadSnap<boolean>("quotes-on") ?? false;
// helpdesk (ticketing) is a CONNECTOR mode like CRM — never counted as CRM itself (own pill).
const hasCrm = (): boolean => !!meta && meta.connectors.some((c) => c.type !== "knowledge" && c.type !== "sendcloud" && c.type !== "helpdesk" && c.type !== "mailvault");
const helpdeskIds = (): number[] => (meta ? meta.connectors.filter((c) => c.type === "helpdesk").map((c) => c.id) : []);
const hasHelpdesk = (): boolean => helpdeskIds().length > 0;
// MailVault (personal mail archive) — own pill like Helpdesk; NEVER inside CRM (Gery 2026-08-11).
const vaultIds = (): number[] => (meta ? meta.connectors.filter((c) => c.type === "mailvault").map((c) => c.id) : []);
const hasVault = (): boolean => vaultIds().length > 0;
// ‼️ CRM ALWAYS sends this list — never "no list". An empty list means "every connector you are
// granted" on the server, and the agent picks ONE model and ONE tool set BEFORE the first tool runs.
// This used to be conditional on having a vault connector, so an org WITHOUT one put Odoo + Sendcloud
// + Helpdesk + FAQ tools in a single CRM loop (2026-08-15). Mirrors taskpane/src/ui/ConnectView.tsx.
const crmIds = (): number[] => (meta ? meta.connectors.filter((c) => c.type !== "knowledge" && c.type !== "sendcloud" && c.type !== "helpdesk" && c.type !== "mailvault").map((c) => c.id) : []);
export const isModuleMode = (m: BrainMode): boolean => (MODULE_MODES as readonly string[]).includes(m);
const isDataMode = (m: BrainMode): boolean => m === "crm" || isModuleMode(m);
// ── Connector pills (mirrors taskpane/src/lib/brainModes.ts) ──
// An EXTERNAL pill = a system outside RPlay. It carries the admin's LABEL (rename the connector in
// /admin → the pill renames here), sits at the END of the bar behind the user's own data, and shows
// a health dot: green = the system answered the last probe, red = it didn't.
export type Chip = { id: BrainMode; label: string; ext?: boolean; dot?: "ok" | "bad"; info?: string };
/** Which pill does a connector type feed? `null` = not shown on the phone (knowledge = our own FAQ;
 *  the companion has no Shipment tab). */
const connectorModeOf = (type: string): "crm" | "helpdesk" | "vault" | null =>
  type === "knowledge" || type === "sendcloud" ? null : type === "helpdesk" ? "helpdesk" : type === "mailvault" ? "vault" : "crm";
/** Label = the admin LABEL when EXACTLY ONE connector feeds the pill (with several behind one pill
 *  no single label describes it, so the generic name stays). Dot = the probe, once it has landed. */
function connectorChip(id: "crm" | "helpdesk" | "vault", fallback: string): Chip {
  const mine = (meta?.connectors ?? []).filter((c) => connectorModeOf(c.type) === id);
  const rows = mine.map((c) => health.find((h) => h.id === c.id)).filter(Boolean) as HealthRow[];
  const bad = rows.find((r) => !r.ok);
  return {
    id,
    label: mine.length === 1 && mine[0].label.trim() ? mine[0].label.trim() : fallback,
    ext: true,
    dot: bad ? "bad" : rows.length && rows.length === mine.length ? "ok" : undefined,
    info: bad ? bad.info || "Not reachable right now." : rows.map((r) => r.info || "").filter(Boolean).join(" · "),
  };
}
function contentModes(): Chip[] {
  return [
    { id: "chat", label: "Chat" },
    { id: "contacts", label: "Contacts" },
    { id: "tasks", label: "Tasks" },
    { id: "receipts", label: "Receipts" },
    { id: "minutes", label: "Notes" },
    { id: "docs", label: "Docs" },
    ...(quotesOn ? [{ id: "quotes" as BrainMode, label: "Quotes" }] : []),
    ...(hasCrm() ? [connectorChip("crm", "CRM")] : []),
    ...(hasHelpdesk() ? [connectorChip("helpdesk", "Helpdesk")] : []),
    ...(hasVault() ? [connectorChip("vault", "Vault")] : []), // Gery 2026-08-19: vertrouwde naam wint
  ];
}
// The Home launch-bar pills consume the SAME source (Chat default; FAQ is the "?" utility).
export function launchModes(): Chip[] { return contentModes(); }
/** Display label for a route id on the attribution line (M0) — the same labels as the pills,
 *  incl. the admin connector label; FAQ has no pill but is routable via the "?" utility. */
function routeLabel(id: string): string {
  if (id === "faq") return "FAQ"; // one source, one name (Gery 2026-08-19: "FAQ" wint van "Company knowledge")
  if (id === "shipment") return "Shipment"; // no pill on the phone, but the id can occur in data
  const chip = contentModes().find((c) => c.id === id);
  return chip ? chip.label : id;
}

// ── M1 (BRAIN-AUTO-MODE-SPEC v2 §3.1): the Auto ▾ selector replaces the pill row ─────────────
// Parameterised so the Home launch bar renders the SAME selector (Gery 2026-08-19: the home
// search bar still showed the old pills). "auto" = the policy, everything else a source.
export function sourceLabel(v: "auto" | BrainMode): string {
  if (v === "auto") return "✦ Auto";
  if (v === "chat") return "General & web";
  if (v === "faq") return "FAQ";
  return routeLabel(v);
}
/** The popover — provenance sections (RPlay / Connected apps), headers not selectable; the
 *  connector entries keep the admin label + health dot. House .dd pattern, never a native select. */
export function sourcePopHtml(current: "auto" | BrainMode): string {
  const all = contentModes();
  const rplay = all.filter((c) => !c.ext && c.id !== "chat");
  const ext = all.filter((c) => c.ext);
  const opt = (id: string, label: string, on: boolean, dot?: "ok" | "bad", hint?: string): string =>
    `<button class="brain-selopt${on ? " on" : ""}" data-selopt="${id}">${dot ? `<span class="bm-dot ${dot}"></span>` : ""}${esc(label)}${hint ? `<span class="brain-selhint">${esc(hint)}</span>` : ""}</button>`;
  return `<div class="brain-selpop" role="listbox">` +
    opt("auto", "✦ Auto", current === "auto", undefined, "Brain picks the source for each question") +
    opt("chat", "General & web", current === "chat") +
    `<div class="brain-selhead">RPlay</div>` +
    rplay.map((c) => opt(c.id, c.label, current === c.id)).join("") +
    opt("faq", "FAQ", current === "faq") +
    (ext.length ? `<div class="brain-selhead">Connected apps</div>` + ext.map((c) => opt(c.id, c.label, current === c.id, c.dot)).join("") : "") +
    `</div>`;
}
const selectorLabel = (): string => sourceLabel(autoOn() ? "auto" : mode);
const selectorPopHtml = (): string => sourcePopHtml(autoOn() ? "auto" : mode);
function pickSource(v: string): void {
  selOpen = false;
  prevRouted = null; // an explicit pick unroutes the conversation (user choice wins)
  if (v === "auto") { webExplicit = false; if (mode !== "chat") { pickMode("chat"); return; } render(); return; }
  webExplicit = v === "chat";
  if (mode !== (v as BrainMode)) { pickMode(v as BrainMode); return; }
  render();
}
/** ONE pill renderer for the chat bar, the empty-state balloons and the Home launch bar. */
export function modePillHtml(m: Chip, active: boolean): string {
  const title = m.dot ? ` title="${esc(`${m.dot === "ok" ? "Connected" : "Not reachable"}${m.info ? ` · ${m.info}` : ""}`)}"` : "";
  const dot = m.dot ? `<span class="bm-dot ${m.dot}"></span>` : "";
  return `<button class="brain-mode${active ? " on" : ""}${m.ext ? " ext" : ""}" data-mode="${m.id}"${title}>${dot}<span class="bm-lb">${esc(m.label)}</span></button>`;
}

// ── Connector health (the dot) ──
// One call for ALL connectors, fired after the screen is painted and only when this user HAS an
// external connector. Cached for 5 minutes on both sides — a dot must never cost a round-trip per open.
export type HealthRow = { id: number; ok: boolean; info?: string };
let health: HealthRow[] = loadSnap<HealthRow[]>("connect-health") ?? [];
let healthAt = 0; // 0 = only the snapshot so far → the next call really goes out
export async function loadHealth(): Promise<void> {
  if (!meta || Date.now() - healthAt < 5 * 60_000) return;
  if (!meta.connectors.some((c) => c.id > 0 && c.type !== "knowledge")) return;
  try {
    const r = await fetch("/api/connect/health", { credentials: "include" });
    if (!r.ok) return; // auth/transport trouble — the last-known dot stands
    const d = (await r.json()) as { health?: HealthRow[] };
    health = Array.isArray(d.health) ? d.health : [];
    healthAt = Date.now();
    saveSnap("connect-health", health);
    renderModes();
    document.dispatchEvent(new CustomEvent("rp:connect-health"));
  } catch { /* keep the last-known dot */ }
}
// Starter "recipes-as-balloons" for the own-data modes + FAQ (English, like every recipe title).
const MODULE_RECIPES: Record<string, Recipe[]> = {
  contacts: [
    { id: 1, title: "Which firms do I know most people at?", prompt: "Which companies do I have the most contacts at? Give me the top 10." },
    { id: 2, title: "Recently added contacts", prompt: "Show me the contacts I most recently came across." },
    { id: 3, title: "Find a contact", prompt: "Find the contact details for " },
  ],
  tasks: [
    { id: 1, title: "What's overdue?", prompt: "Which of my tasks are overdue? List them by priority." },
    { id: 2, title: "Open tasks per company", prompt: "How many open tasks do I have per company?" },
    { id: 3, title: "High-priority open work", prompt: "List my open and in-progress high-priority tasks." },
  ],
  receipts: [
    { id: 1, title: "Spend this year", prompt: "What did I spend in total this year, broken down by month?" },
    { id: 2, title: "Top suppliers", prompt: "Who are my top suppliers by total amount this year?" },
    { id: 3, title: "Not yet forwarded", prompt: "Which receipts haven't been forwarded to bookkeeping yet?" },
  ],
  minutes: [
    { id: 1, title: "Recent meetings", prompt: "Show me my most recent meeting notes." },
    { id: 2, title: "Find a meeting", prompt: "Find the notes of the meeting about " },
    { id: 3, title: "Meetings with a person", prompt: "Which meetings did I have with " },
  ],
  faq: [
    { id: 1, title: "Ask the company FAQ", prompt: "What does our company say about " },
    { id: 2, title: "Look up a policy", prompt: "What is our policy on " },
    { id: 3, title: "Search our documents", prompt: "Search our company documents for " },
  ],
  docs: [
    { id: 1, title: "Find a document", prompt: "Find my document about " },
    { id: 2, title: "Search my documents", prompt: "Which of my documents mention " },
    { id: 3, title: "Summarise a document", prompt: "Give me a summary of my document about " },
  ],
  quotes: [
    { id: 1, title: "Find a quote", prompt: "Find our quote for " },
    { id: 2, title: "Open proposals", prompt: "Which of our quotes are still open (not yet won or lost)?" },
    { id: 3, title: "Review a proposal", prompt: "What's in our proposal for " },
  ],
  // helpdesk is a CONNECTOR mode (not in MODULE_MODES) — plain record key, resolved explicitly.
  helpdesk: [
    { id: 1, title: "Show open tickets", prompt: "Show me the open tickets, newest first." },
    { id: 2, title: "Draft a reply to a ticket", prompt: "Read ticket " },
    { id: 3, title: "Search the helpdesk KB", prompt: "Search the helpdesk knowledge base for " },
  ],
};

// ── house sounds (ported from taskpane/src/lib/chatSounds.ts) ──
// Master on/off, synced from the account (Settings → Sounds on the pane/PWA → /api/session →
// the session snapshot, written by boot()). Default ON; read live so a flip on another surface
// takes effect the next time this device probes.
function soundsOn(): boolean {
  try { return loadSnap<{ audio_feedback?: boolean }>("session")?.audio_feedback !== false; }
  catch { return true; }
}
let actx: AudioContext | null = null;
function sweep(from: number, to: number): void {
  if (!soundsOn()) return;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    if (!actx) actx = new AC();
    if (actx.state === "suspended") void actx.resume().catch(() => {});
    const now = actx.currentTime;
    const osc = actx.createOscillator();
    const gain = actx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(from, now);
    osc.frequency.exponentialRampToValueAtTime(to, now + 0.12);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.16, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    osc.connect(gain).connect(actx.destination);
    osc.start(now);
    osc.stop(now + 0.18);
  } catch { /* audio not allowed yet */ }
}
const plopSend = (): void => sweep(740, 420);
const plopDone = (): void => sweep(470, 700);

let loopEl: HTMLAudioElement | null = null;
let fadeTimer: ReturnType<typeof setInterval> | null = null;
let loopWanted = false; // guards the async play() race: stop during a pending play() used
                        // to let the loop start AFTER the answer landed and run forever
const LOOP_VOLUME = 0.14;
function thinkingStart(): void {
  if (!soundsOn()) return;
  try {
    loopWanted = true;
    if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
    if (!loopEl) { loopEl = new Audio("/sounds/thinking.m4a"); loopEl.loop = true; loopEl.preload = "auto"; }
    const a = loopEl;
    a.volume = LOOP_VOLUME;
    a.currentTime = 0;
    void a.play().then(() => { if (!loopWanted) { a.pause(); a.currentTime = 0; } }).catch(() => {});
  } catch { /* no audio */ }
}
function thinkingStop(): void {
  loopWanted = false;
  const a = loopEl;
  if (!a) return;
  if (a.paused) { a.currentTime = 0; return; }
  // Fade where the platform allows it; iOS IGNORES volume writes on media elements, so
  // the hard-stop timeout below is the stop that actually counts there.
  if (fadeTimer) clearInterval(fadeTimer);
  fadeTimer = setInterval(() => {
    if (!loopEl) return;
    if (loopWanted) { if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; } return; }
    const v = Math.max(0, loopEl.volume - LOOP_VOLUME / 6);
    try { loopEl.volume = v; } catch { /* iOS read-only volume */ }
    if (v <= 0.001) { if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; } loopEl.pause(); loopEl.currentTime = 0; }
  }, 30);
  setTimeout(() => {
    if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
    if (!loopWanted && loopEl && !loopEl.paused) {
      loopEl.pause();
      loopEl.currentTime = 0;
      try { loopEl.volume = LOOP_VOLUME; } catch { /* iOS */ }
    }
  }, 240);
}

// ── structured answer formatter (mirror of ConnectView.renderAnswer, string-HTML) ──
// [label](url) links + bare-URL fallback in ONE alternation pass (web citation contract, on the
// ESCAPED string — href keeps &amp;; a markdown-consumed URL is never linkified twice), then **bold**.
const hostLabel = (u: string): string => { try { return new URL(u.replace(/&amp;/g, "&")).hostname.replace(/^www\./, ""); } catch { return "link"; } };
const bold = (s: string): string =>
  esc(s)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)<>"']+)\)|(https?:\/\/[^\s<>"')]+)/g, (_m, label, url, bare) =>
      bare
        ? `<a href="${bare}" target="_blank" rel="noreferrer">${hostLabel(bare)}</a>`
        : `<a href="${url}" target="_blank" rel="noreferrer">${label}</a>`)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
function answerHtml(text: string): string {
  const out: string[] = [];
  let list: string[] = [];
  let listType: "ul" | "ol" = "ul";
  const flush = () => {
    if (!list.length) return;
    out.push(`<${listType} class="cn-${listType}">${list.join("")}</${listType}>`);
    list = [];
  };
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    const mB = line.match(/^[•\-*]\s+(.*)$/);
    const mN = line.match(/^\d{1,3}[.)]\s+(.*)$/);
    if (mB || mN) {
      const t: "ul" | "ol" = mB ? "ul" : "ol";
      if (list.length && listType !== t) flush();
      listType = t;
      list.push(`<li>${mailBtns(bold(mB ? mB[1] : mN![1]))}</li>`);
      continue;
    }
    if (/^[→›↳]/.test(line) && list.length) { list.push(`<li class="cn-cont">${mailBtns(bold(line.replace(/^[→›↳]\s*/, "")))}</li>`); continue; }
    flush();
    const mH = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    if (mH) { out.push(`<div class="cn-h">${esc(mH[1])}</div>`); continue; }
    out.push(`<p class="cn-p">${mailBtns(bold(line))}</p>`);
  }
  flush();
  return out.join("");
}

// ── MCP-Apps card host (SEP-1865) — vault cards on the phone ─────────────────────────────────
// The client half of the apps host, speaking the same 2026-01-26 dialect as the pane/PWA
// (taskpane/src/ui/McpAppCard.tsx) over postMessage:
//   view → host  ui/initialize                    (a request, with an id)
//   host → view  result { hostContext }           (theme + displayMode + dimensions)
//   view → host  ui/notifications/initialized
//   host → view  ui/notifications/tool-result     { content, structuredContent, _meta }
//   view → host  ui/notifications/size-changed    (card measures itself → iframe height)
//   view → host  tools/call                       (a card button → /api/connect/ui-call)
//
// ‼️ WHY the card is not simply an item in the parts[] flow: render() rebuilds the thread's
// innerHTML on every event, and an <iframe> that is re-created — or even MOVED in the DOM —
// reloads from scratch and loses its handshake state. So cards live in a PERSISTENT layer
// (.cn-cards, an absolutely-positioned child of the scrolling thread that render() never
// touches); the rebuilt flow only carries a fixed-height placeholder slot per card, and each
// host is pinned over its slot after every render. Absolutely-positioned children of a scroll
// container move with its content, so a pinned card scrolls exactly like a bubble.
//
// ‼️ sandbox="allow-scripts" WITHOUT allow-same-origin (pane parity): the frame runs in an
// opaque origin, so it cannot read this page, its storage or the session cookie — postMessage
// stays the only way across. Buttons go through /api/connect/ui-call, which resolves the
// paired user's identity server-side; the frame can only ask, never choose who it is.
type McpApp = { connector_id: number; tool: string; resource_uri: string; html: string; content: unknown[]; structuredContent: unknown; _meta: unknown };
type McpCard = { host: HTMLElement; frame: HTMLIFrameElement; app: McpApp; height: number; reported?: boolean };
let mcpSeq = 0;
const mcpCards = new Map<string, McpCard>();
/** The app's current theme — main.ts always stamps [data-theme] on <html>; the OS preference
 *  is only a fallback for a first paint that somehow beat it. */
function mcpTheme(): "dark" | "light" {
  const t = document.documentElement.getAttribute("data-theme");
  if (t === "dark" || t === "light") return t;
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
/** The thread's two layers: .cn-cards (persistent iframe hosts) + .cn-body (rebuilt per render).
 *  .cn-body is display:contents, so the flow items keep laying out in the thread's own flex
 *  column exactly as before — the extra wrapper has no box of its own. */
function mcpLayers(thread: HTMLElement): { cards: HTMLElement; body: HTMLElement } {
  let cards = thread.querySelector<HTMLElement>(":scope > .cn-cards");
  let body = thread.querySelector<HTMLElement>(":scope > .cn-body");
  if (!cards) { cards = document.createElement("div"); cards.className = "cn-cards"; thread.appendChild(cards); }
  if (!body) { body = document.createElement("div"); body.className = "cn-body"; thread.appendChild(body); }
  return { cards, body };
}
/** Pin every card host over its placeholder slot. Heights first, positions second — a height
 *  change on an earlier slot moves every slot below it, so measuring in one pass would pin
 *  later cards to stale offsets. */
function positionMcpCards(): void {
  if (!mcpCards.size) return;
  const thread = $("#cnThread");
  if (!thread) return;
  for (const [key, c] of mcpCards) {
    const slot = thread.querySelector<HTMLElement>(`[data-mcp-slot="${key}"]`);
    if (slot && slot.style.height !== `${c.height}px`) slot.style.height = `${c.height}px`;
  }
  const tr = thread.getBoundingClientRect();
  for (const [key, c] of mcpCards) {
    const slot = thread.querySelector<HTMLElement>(`[data-mcp-slot="${key}"]`);
    if (!slot) { c.host.style.display = "none"; continue; } // no slot this render (cleared thread mid-sync)
    const sr = slot.getBoundingClientRect();
    c.host.style.display = "";
    c.host.style.top = `${sr.top - tr.top + thread.scrollTop}px`;
    c.host.style.left = `${sr.left - tr.left + thread.scrollLeft}px`;
    c.host.style.width = `${sr.width}px`;
    c.host.style.height = `${c.height}px`;
  }
}
/** Reconcile the persistent layer against `items`: create hosts for new cards, drop hosts whose
 *  card left the thread (clear chat / new mode), then pin everything. Called after every render. */
function syncMcpCards(thread: HTMLElement): void {
  const { cards } = mcpLayers(thread);
  const live = new Set<string>();
  for (const it of items) if (it.kind === "mcp_app") live.add(it.key);
  for (const [key, c] of mcpCards) if (!live.has(key)) { c.host.remove(); mcpCards.delete(key); }
  for (const it of items) {
    if (it.kind !== "mcp_app" || mcpCards.has(it.key)) continue;
    const host = document.createElement("div");
    host.className = "cn-mcp-host";
    const frame = document.createElement("iframe");
    frame.className = "mcp-app-frame";
    frame.title = "Vault answer card";
    frame.setAttribute("sandbox", "allow-scripts"); // ‼️ never allow-same-origin — see the header note
    frame.srcdoc = it.app.html;
    host.appendChild(frame);
    cards.appendChild(host);
    mcpCards.set(it.key, { host, frame, app: it.app, height: 560 }); // 560 = pane's pre-handshake floor
  }
  positionMcpCards();
}
/** ONE window listener serves every card — a card is recognised by its frame's contentWindow. */
async function onMcpMessage(ev: MessageEvent): Promise<void> {
  let card: McpCard | undefined;
  for (const c of mcpCards.values()) if (c.frame.contentWindow === ev.source) { card = c; break; }
  if (!card) return;
  const m = ev.data as { method?: string; id?: unknown; params?: { height?: unknown; name?: unknown; arguments?: unknown } } | null;
  if (!m || typeof m !== "object") return;
  const post = (o: Record<string, unknown>): void => card!.frame.contentWindow?.postMessage({ jsonrpc: "2.0", ...o }, "*");
  if (m.method === "ui/initialize") {
    post({
      id: m.id,
      result: {
        protocolVersion: "2026-01-26",
        hostCapabilities: { serverTools: {}, serverResources: {}, logging: {} },
        hostInfo: { name: "rplay-brain", version: "1.0.0" },
        hostContext: {
          theme: mcpTheme(),
          displayMode: "inline",
          containerDimensions: { width: card.host.clientWidth || 360, maxHeight: 1200 },
        },
      },
    });
    return;
  }
  if (m.method === "ui/notifications/initialized") {
    const a = card.app;
    post({ method: "ui/notifications/tool-result", params: { content: a.content, structuredContent: a.structuredContent, _meta: a._meta } });
    // ‼️ Measured 2026-08-30 in a sandboxed srcdoc frame: some engines never deliver the card's
    // ResizeObserver callbacks on their own — not even the initial observation, not even on real
    // content growth — yet a viewport change imposed from OUTSIDE the frame does reach it. If no
    // size report has landed shortly after the payload went in, nudge the frame width by half a
    // pixel for one rAF tick: the card re-measures and reports. Where the observer works
    // normally, the extra fire is a harmless repeat of a height already applied.
    // setTimeout on both edges, NOT requestAnimationFrame: rAF never runs on a hidden page, so
    // an rAF-scheduled restore would leave the frame half a pixel narrow until the next paint.
    const c = card;
    setTimeout(() => {
      if (c.reported || !c.frame.isConnected) return;
      c.frame.style.width = "calc(100% - 0.5px)";
      setTimeout(() => { c.frame.style.width = ""; }, 60);
    }, 400);
    return;
  }
  // The card measures its own content (the host cannot look inside an opaque-origin frame) and
  // reports it here; the slot takes the height so the flow reflows, the host follows the slot.
  // Same clamp as the pane: a misbehaving card must not swallow the page.
  if (m.method === "ui/notifications/size-changed") {
    const h = Number(m.params?.height);
    if (Number.isFinite(h) && h > 0) {
      card.reported = true;
      card.height = Math.min(Math.max(Math.ceil(h), 240), 2600);
      positionMcpCards();
      const thread = $("#cnThread");
      if (thread && nearBottom) thread.scrollTop = thread.scrollHeight;
    }
    return;
  }
  // A button in a card is a tools/call, made with the USER'S session — never wider. The card
  // awaits this id to flip its button to ✓ or !; a host that only logs leaves every button
  // spinning into its timeout (chassis, 30-08).
  if (m.method === "tools/call" && m.params && typeof m.params.name === "string") {
    try {
      const r = await fetch("/api/connect/ui-call", {
        method: "POST", credentials: "include", headers: { "content-type": "application/json" },
        body: JSON.stringify({ connector_id: card.app.connector_id, name: m.params.name, arguments: m.params.arguments ?? {} }),
      });
      const data = (await r.json().catch(() => null)) as { result?: unknown; error?: string } | null;
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      post({ id: m.id, result: data?.result });
    } catch (e) {
      post({ id: m.id, error: { code: -32000, message: String((e as Error).message || e) } });
    }
  }
}

// ── render ──
function lastCompleteAnswer(): Extract<Item, { kind: "assistant" }> | undefined {
  let idx = -1;
  for (let i = items.length - 1; i >= 0; i--) if (items[i].kind === "assistant") { idx = i; break; }
  if (idx < 0) return undefined;
  if (items.slice(idx + 1).some((i) => i.kind === "notice" || i.kind === "error")) return undefined;
  return items[idx] as Extract<Item, { kind: "assistant" }>;
}

// In-chat helpdesk cards (spec §3.5 UX): ticket list + thread streamed as artifacts by the tools.
function hdTicketsHtml(it: Extract<Item, { kind: "hd_tickets" }>): string {
  const rows = it.tickets.map((t) => {
    const who = t.customer?.name || t.customer?.email || "unknown customer";
    return `<div class="hd-row"><div class="hd-main">` +
      `<div class="hd-ref">${esc(t.ref)} · ${esc(hdAgo(t.updatedAt))}</div>` +
      `<div class="hd-sub">${esc(t.subject)}</div>` +
      `<div class="hd-who">${esc(who)}</div>` +
      `<div class="hd-acts"><button class="cn-sample hd-mini" data-prompt="Show me ticket ${esc(t.ref)} (id ${t.id}) in full.">Open</button>` +
      `<button class="cn-sample hd-mini" data-prompt="Read ticket ${esc(t.ref)} (id ${t.id}) and draft a reply to the customer.">Draft reply</button>` +
      `<button class="cn-sample hd-mini" data-prompt="Propose closing ticket ${esc(t.ref)} (id ${t.id}) as resolved.">Close</button>` +
      `<button class="cn-sample hd-mini bad" data-prompt="Propose deleting ticket ${esc(t.ref)} (id ${t.id}) as spam.">Delete</button></div>` +
      `</div><span class="hd-stage">${esc(t.stage)}</span></div>`;
  }).join("");
  return `<div class="hd-snap in-chat"><div class="hd-snap-hd"><span>${it.tickets.length === 1 ? "Ticket" : "Tickets"}${it.team ? ` — ${esc(it.team)}` : ""}</span><span class="hd-snap-n">${it.tickets.length}</span></div>${rows}</div>`;
}
function hdThreadHtml(it: Extract<Item, { kind: "hd_ticket" }>): string {
  const t = it.ticket;
  const msgs = [
    ...(t.description ? [`<div class="hd-msg in">${esc(String(t.description))}</div>`] : []),
    ...((t.messages || []).map((m) => `<div class="hd-msg ${m.direction === "in" ? "in" : "out"}"><div class="hd-msg-who">${m.direction === "in" ? "CUSTOMER" : "SUPPORT"} · ${esc(m.author)} · ${esc(hdAgo(m.at))}</div>${esc(m.body)}</div>`)),
  ].join("");
  return `<div class="hd-snap in-chat hd-thread"><div class="hd-snap-hd"><span>${esc(t.ref)} — ${esc(t.subject)}</span><span class="hd-stage">${esc(t.stage)}</span></div>` +
    `<div class="hd-who" style="margin-bottom:6px">${esc(t.customer?.name || "")}${t.customer?.email ? ` · ${esc(t.customer.email)}` : ""}</div>${msgs}` +
    `<div class="hd-acts" style="margin-top:8px"><button class="cn-sample hd-mini" data-prompt="Draft a reply to the customer on ticket ${esc(t.ref)} (id ${t.id}).">Draft reply</button>` +
    `<button class="cn-sample hd-mini" data-prompt="Propose closing ticket ${esc(t.ref)} (id ${t.id}) as resolved.">Close…</button>` +
    `<button class="cn-sample hd-mini bad" data-prompt="Propose deleting ticket ${esc(t.ref)} (id ${t.id}) as spam.">Delete…</button></div></div>`;
}

// KB-matches card: expandable Q&A rows with in-place edit (an incomplete entry gets corrected
// right where it's noticed; PUT /api/faq re-embeds when the question changes).
function hdKbHtml(it: Extract<Item, { kind: "hd_kb" }>, idx: number): string {
  const rows = it.hits.map((h, k) => {
    const head = `<button class="hd-kb-q" data-kb-open="${idx}:${k}"><span class="hd-stage">${h.pct}%</span><span class="hd-kb-qt">${esc(h.question)}</span><span class="hd-kb-cv">${it.open === k ? "▾" : "▸"}</span></button>`;
    let body = "";
    if (it.open === k && it.edit !== k) {
      body = `<div class="hd-kb-a">${esc(h.answer)}</div><div class="hd-acts" style="margin-top:6px"><button class="cn-sample hd-mini" data-kb-edit="${idx}:${k}">Edit this entry</button>${h.product ? `<span class="hd-who">${esc(h.product)}</span>` : ""}</div>`;
    } else if (it.edit === k) {
      body = `<input class="hd-kb-in" data-kb-q="${idx}" value="${esc(h.question)}" placeholder="Question">` +
        `<textarea class="hd-kb-in" data-kb-a="${idx}" rows="4" placeholder="Answer">${esc(h.answer)}</textarea>` +
        (it.err ? `<div class="cn-cf-note err">${esc(it.err)}</div>` : "") +
        `<div class="hd-acts" style="margin-top:4px"><button class="cn-sample hd-mini" data-kb-save="${idx}:${k}"${it.saving ? " disabled" : ""}>${it.saving ? "Saving…" : "Save to KB"}</button><button class="cn-sample hd-mini" data-kb-cancel="${idx}"${it.saving ? " disabled" : ""}>Cancel</button></div>`;
    }
    return `<div class="hd-row" style="display:block">${head}${body}</div>`;
  }).join("");
  let addBlock = "";
  if (!it.hits.length) {
    const intro = `<div class="hd-kb-a">No KB entry matches${it.query ? ` "<strong>${esc(it.query)}</strong>"` : ""} yet.</div>`;
    if (it.added) addBlock = `${intro}<div class="hd-kb-a" style="margin-top:6px">✅ Added to the helpdesk KB — retrievable immediately.</div>`;
    else if (it.adding) addBlock = `${intro}<input class="hd-kb-in" data-kb-nq="${idx}" value="${esc(it.query || "")}" placeholder="Question (generalized)">` +
      `<textarea class="hd-kb-in" data-kb-na="${idx}" rows="4" placeholder="Answer (instructive)"></textarea>` +
      (it.err ? `<div class="cn-cf-note err">${esc(it.err)}</div>` : "") +
      `<div class="hd-acts" style="margin-top:4px"><button class="cn-sample hd-mini" data-kb-addsave="${idx}"${it.saving ? " disabled" : ""}>${it.saving ? "Saving…" : "Save to KB"}</button><button class="cn-sample hd-mini" data-kb-addcancel="${idx}"${it.saving ? " disabled" : ""}>Cancel</button></div>`;
    else addBlock = `${intro}<div class="hd-acts" style="margin-top:6px"><button class="cn-sample hd-mini" data-kb-add="${idx}">Add KB entry</button></div>`;
  }
  return `<div class="hd-snap in-chat hd-kb"><div class="hd-snap-hd"><span>${it.hits.length ? "Helpdesk KB — matched entries" : "Helpdesk KB — no match"}</span><span class="hd-snap-n">${it.hits.length}</span></div>${addBlock}${rows}</div>`;
}
async function addKbEntry(idx: number): Promise<void> {
  const it = items[idx];
  if (!it || it.kind !== "hd_kb" || it.saving) return;
  const q = document.querySelector<HTMLInputElement>(`[data-kb-nq="${idx}"]`)?.value.trim() || "";
  const a = document.querySelector<HTMLTextAreaElement>(`[data-kb-na="${idx}"]`)?.value.trim() || "";
  if (!q || !a) return;
  it.saving = true; it.err = undefined; render();
  try {
    const r = await fetch("/api/faq", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ question: q, answer: a, category: "helpdesk", meta: { source: "manual" } }) });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    it.adding = false; it.added = true;
  } catch (e) { it.err = String((e as Error)?.message || e); }
  it.saving = false; render();
}
async function saveKbEdit(idx: number, k: number): Promise<void> {
  const it = items[idx];
  if (!it || it.kind !== "hd_kb" || it.saving) return;
  const hit = it.hits[k];
  const q = document.querySelector<HTMLInputElement>(`[data-kb-q="${idx}"]`)?.value.trim() || "";
  const a = document.querySelector<HTMLTextAreaElement>(`[data-kb-a="${idx}"]`)?.value.trim() || "";
  if (!hit || !q || !a) return;
  it.saving = true; it.err = undefined; render();
  try {
    const r = await fetch("/api/faq", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ id: hit.id, question: q, answer: a }) });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    hit.question = q; hit.answer = a;
    it.edit = null; it.open = k;
  } catch (e) { it.err = String((e as Error)?.message || e); }
  it.saving = false; render();
}

// Approval card for a proposed helpdesk write (send/close/delete/save-to-KB). Delete = the red
// destructive variant. Approve posts the signed token to /api/connect/helpdesk-confirm — the ONLY
// place the write executes; Cancel just marks the card (nothing was sent).
function confirmHtml(it: Extract<Item, { kind: "confirm" }>, idx: number): string {
  const destructive = it.action === "helpdesk_delete";
  const approveLabel = it.action === "helpdesk_reply" ? "Approve &amp; send"
    : it.action === "helpdesk_close" ? "Approve &amp; close"
      : destructive ? "Delete permanently"
        : it.action === "helpdesk_save_faq" ? "Save to helpdesk KB" : "Approve";
  const head = it.state === "done" ? "✓ Done" : it.state === "cancelled" ? "Cancelled" : it.state === "error" ? "✗ Failed"
    : destructive ? "⚠ Permanent delete — this cannot be undone" : "Needs your approval — nothing is sent yet";
  const body = typeof it.preview?.body === "string" && it.preview.body
    ? (it.action === "helpdesk_reply" && it.state === "pending" && it.editing
        ? `<textarea class="cn-cf-body cn-cf-edit" data-cf-edit="${idx}" rows="7">${esc(String(it.preview.body))}</textarea><div class="cn-cf-note">What you see here is exactly what gets sent.</div>`
        : `<div class="cn-cf-body">${esc(String(it.preview.body))}</div>`)
    : "";
  const kb = it.preview?.kb ? `<div class="cn-cf-body"><strong>Q:</strong> ${esc(String(it.preview.question || ""))}<br><strong>A:</strong> ${esc(String(it.preview.answer || ""))}</div>` : "";
  // Same field table as the pane/PWA card (ConnectView.tsx): what you approve, you can see. Shipment
  // isn't offered on the phone yet, but the card is shared — keep the three clients identical.
  const rows = Array.isArray(it.preview?.fields) ? (it.preview!.fields as Array<{ k: string; v: string }>) : [];
  const fields = rows.length
    ? `<table class="cn-cf-fields">${rows.map((f) => `<tr><th>${esc(String(f.k))}</th><td>${esc(String(f.v))}</td></tr>`).join("")}</table>`
    : "";
  const acts = it.state === "pending"
    ? `<div class="cn-cf-acts" style="flex-wrap:wrap"><button class="cn-cf-go${destructive ? " bad" : ""}" data-cf-approve="${idx}">${approveLabel}</button>${it.action === "helpdesk_reply" ? `<button class="cn-cf-go ok" data-cf-approve-close="${idx}" title="Send the reply and move the ticket to the closing stage in one go">Approve, send &amp; resolve</button>` : ""}${it.action === "helpdesk_reply" && !it.editing ? `<button class="cn-cf-x" data-cf-editmode="${idx}">Edit</button>` : ""}<button class="cn-cf-x" data-cf-cancel="${idx}">Cancel</button></div>${destructive ? `<div class="cn-cf-note">Not spam? Cancel and ask me to close it instead.</div>` : ""}`
    : it.state === "working" ? `<div class="cn-cf-note">Working…</div>`
      : it.state === "done" ? `<div class="cn-cf-note">${esc(it.resultMsg || "Done.")}</div>`
        : it.state === "cancelled" ? `<div class="cn-cf-note">Cancelled — nothing was sent or changed.</div>`
          : `<div class="cn-cf-note err">${esc(it.error || "failed")}</div>`;
  return `<div class="cn-confirm${destructive && it.state === "pending" ? " bad" : ""}"><div class="cn-cf-head${destructive && it.state === "pending" ? " bad" : ""}">${head}</div><div class="cn-cf-sum">${esc(it.summary)}</div>${fields}${body}${kb}${acts}</div>`;
}
async function approveConfirmItem(idx: number, andClose = false): Promise<void> {
  const it = items[idx];
  if (!it || it.kind !== "confirm" || it.state !== "pending") return;
  // Human edit at approve time (helpdesk_reply): what's in the textarea is what gets sent.
  const editEl = document.querySelector<HTMLTextAreaElement>(`[data-cf-edit="${idx}"]`);
  const edited = it.action === "helpdesk_reply" && editEl && editEl.value.trim() && editEl.value !== String(it.preview?.body || "")
    ? editEl.value.trim() : "";
  if (edited && it.preview) it.preview.body = edited; // the card keeps showing what was actually sent
  it.state = "working"; render();
  try {
    const r = await fetch("/api/connect/helpdesk-confirm", {
      method: "POST", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ connector_id: it.connectorId, token: it.token, ...(edited ? { edited_body: edited } : {}), ...(andClose ? { and_close: true } : {}) }),
    });
    const data = (await r.json().catch(() => ({}))) as { ok?: boolean; result?: { message?: string }; error?: string };
    if (r.ok && data.ok) {
      it.state = "done"; it.resultMsg = data.result?.message || "Done.";
      // Mutates IN PLACE — reordering made the thread jump under the user's finger.
      // Hand the confirmed result back into the CONVERSATION: the model reads this '✅ …' line
      // as ground truth (its contract says so) and offers the next step instead of "waiting".
      const ref = typeof it.preview?.ticket_ref === "string" && it.preview.ticket_ref ? `${it.preview.ticket_ref} — ` : "";
      items.push({ kind: "assistant", text: `✅ ${ref}${it.resultMsg}` });
      // Never leave the user stranded: a done action always offers tappable next steps.
      items.push({ kind: "choices", prompt: null, options: [{ label: "Show open tickets" }, { label: "Show resolved tickets" }] });
    } else { it.state = "error"; it.error = data.error || `HTTP ${r.status}`; }
  } catch (e) { it.state = "error"; it.error = String((e as Error)?.message || e); }
  render();
}

// Stick-to-bottom ONLY when the user already is at the bottom — interacting with a card higher
// up (Approve/Edit/KB edit) must not yank the view down (Gery 2026-07-06).
let nearBottom = true;
export function trackThreadScroll(): void {
  const el = $("#cnThread");
  if (el) nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
}
function render(): void {
  const thread = $("#cnThread");
  if (!thread || !meta) return;
  ensureCopyStyle();
  const parts: string[] = [];
  if (!items.length) {
    parts.push(`<div class="cn-empty">${modeBalloonsHtml()}${emptyHintHtml()}${mode === "helpdesk" ? hdSnapHtml() : ""}${samplesHtml()}</div>`);
  }
  let lastAiIdx = -1;
  for (let k = items.length - 1; k >= 0; k--) if (items[k].kind === "assistant") { lastAiIdx = k; break; }
  const ans = lastCompleteAnswer();
  // Mail/Excel for the last complete answer — rendered INSIDE that answer's action row (one group), so
  // the row reads: Copy (icon) · Ask Expert · Mail me · Excel, all aligned. Same ids → same handlers.
  const mailExcelHtml = `<button class="cn-act" id="cnMailMe"${mailState === "sending" || mailState === "sent" ? " disabled" : ""}>${mailState === "sent" ? "✓ Sent to your mailbox" : mailState === "sending" ? "Sending…" : mailState === "error" ? "Sending failed — try again" : "✉ Mail me this answer"}</button>${isDataMode(mode) ? `<button class="cn-act" id="cnXls"${xlsState === "building" || xlsState === "done" || xlsState === "none" ? " disabled" : ""}>${xlsState === "done" ? "✓ Excel downloaded" : xlsState === "building" ? "Building Excel…" : xlsState === "none" ? "No table in this answer" : xlsState === "error" ? "Excel failed — try again" : "⬇ Excel download"}</button>` : ""}`;
  items.forEach((it, idx) => {
    if (it.kind === "user") parts.push(`<div class="cn-msg user">${esc(it.text)}</div>`);
    else if (it.kind === "thinking") parts.push(thinkPanelHtml(it, idx));
    // assistant bubble + per-answer actions. CRM has 3 states at the Ask Expert spot:
    //  · running  — this answer's deep run is in flight → "Analysing… ✉ Mail me when ready"
    //  · steering — the inline steer box is open for this answer
    //  · idle     — Copy · Ask Expert · Mail · Excel (one row; Mail/Excel on the last answer)
    else if (it.kind === "assistant") {
      let row: string;
      if (mode === "crm" && expertRunningFrom === idx) {
        row = `<div class="cn-acts-row cn-expert-run"><span class="cn-run-lb"><span class="cn-run-dot"></span>Analysing…</span><button class="cn-act" data-mailwhen>✉ Mail me when ready</button></div>`;
      } else if (mode === "crm" && expertOpenIdx === idx) {
        row = `<div class="cn-acts-row cn-steer"><input class="cn-steer-in" id="cnSteerIn" type="text" placeholder="Steer this analysis (optional)…" value="${esc(expertSteer)}"><button class="cn-expert cn-steer-go" data-steer-go="${idx}">✦ Ask Expert →</button><button class="cn-steer-x" data-steer-x title="Cancel" aria-label="Cancel">✕</button></div>`;
      } else if (busy && idx === lastAiIdx) {
        row = "";
      } else {
        const expertBtn = mode === "crm" && meta?.expertAllowed && !it.expert ? askExpertBtnHtml(idx) : "";
        const acts = idx === lastAiIdx && !busy && ans ? mailExcelHtml : "";
        row = `<div class="cn-acts-row">${copyBtnHtml(idx)}${expertBtn}${acts}</div>`;
      }
      parts.push(`<div class="cn-ai"><div class="cn-msg ai">${answerHtml(it.text)}</div>${row}</div>`);
    }
    else if (it.kind === "status") {
      // ‼️ ONE STRIP, not one row per call — the same shape as the pane (ConnectView
      // groupStatuses). Seven calls as seven full-width rows push the answer off a phone screen
      // entirely, and they are one activity rather than seven events. Consecutive calls are
      // folded into the strip opened by the first of them; the leading marker carries the TOTAL,
      // which is the number worth knowing and one no single chip can show.
      const prev = items[idx - 1];
      if (prev && prev.kind === "status") return; // already folded into the strip above (forEach: return, not continue)
      const run: Extract<Item, { kind: "status" }>[] = [];
      for (let k = idx; k < items.length; k++) {
        const x = items[k];
        if (x.kind !== "status") break;
        run.push(x);
      }
      const done = run.filter((c) => c.ms != null);
      const total = done.length ? done.reduce((a, c) => a + (c.ms || 0), 0) : null;
      const running = run.some((c) => c.state === "run");
      const chips = run.map((c, k) =>
        `${k > 0 ? '<span class="cn-strip-sep">…</span>' : ""}` +
        `<span class="cn-strip-call ${esc(c.state)}" title="${esc(c.connector)} · ${esc(c.tool)}">` +
        `${esc(c.tool)}${c.state === "run" ? " …" : c.ms != null ? ` ${(c.ms / 1000).toFixed(1)}s` : ""}` +
        `${c.state === "denied" ? " · no access" : c.state === "error" ? " · error" : ""}</span>`).join("");
      parts.push(
        `<div class="cn-strip">` +
        `<span class="cn-strip-head">${running ? '<span class="cn-dots">…</span>' : "…"}` +
        `${total != null ? ` ${(total / 1000).toFixed(1)}s` : ""}</span>${chips}</div>`,
      );
    }
    else if (it.kind === "routed") {
      // M1: the routed-topic chip; ✕ appends a General & web re-run under a badge (never erases).
      parts.push(`<div class="cn-routed${it.corrected ? " corrected" : ""}"><span class="cn-routed-label">→ ${esc(routeLabel(it.mode))}</span>${!busy && !it.corrected ? `<button class="cn-routed-x" data-routedx="${idx}" title="Not this topic — answer in general chat instead" aria-label="Answer in general chat instead">✕</button>` : ""}</div>`);
    }
    else if (it.kind === "retried") {
      parts.push(`<div class="cn-retried"><b>Retried</b> in ${esc(it.mode === "chat" ? "General & web" : routeLabel(it.mode))}</div>`);
    }
    else if (it.kind === "srcsum") {
      // M0 attribution (spec §3.3): the honest "Searched: …" line — execution, never the plan.
      // M1.1: + "Try another source ▾" (append-only re-run; parity with the pane/PWA).
      // ‼️ "Not searched" is a warning, not a footnote — it contradicts the answer right above it.
      // In the same faint grey it lost that argument every time (Gery 2026-08-20: the model listed
      // the terms it had supposedly searched for, this line said the source was never queried).
      // How long the TURN took — the number Gery actually asks for ("hoe lang duurde die beurt"),
      // and the only one that answers it. The tool strip's total is the SUM of the tool calls,
      // which on a slow turn is the small half: the archive answers in 0.6s and the model spends
      // the other forty seconds. The server has sent this all along; no client showed it.
      const took = typeof it.ms === "number" ? ` · <span class="cn-srcsum-ms">${(it.ms / 1000).toFixed(1)}s</span>` : "";
      const inner = it.attempts.length
        ? `Searched: ${it.attempts.map((a) => `<b>${esc(a.source)}</b>${a.status === "error" ? '<span class="cn-srcsum-err"> (failed)</span>' : ""}`).join(" · ")}${took}`
        : `⚠ <b>Not searched.</b> This answer comes from the conversation — <b>${esc(routeLabel(it.route))}</b> was never queried, so treat anything above that reads like a search result as unverified.`;
      const retry = !busy && !it.retried
        ? `<button class="cn-srcsum-retry" data-srcretry="${idx}">Try another source ▾</button>`
        : "";
      const opts = it.retryOpen && !it.retried
        ? `<span class="cn-srcsum-opts">` +
          [{ id: "chat", label: "General & web" }, ...contentModes().filter((c) => c.id !== "chat").map((c) => ({ id: c.id as string, label: c.label })), { id: "faq", label: "FAQ" }]
            .filter((o) => o.id !== it.route)
            .map((o) => `<button class="cn-srcsum-opt" data-srcpick="${idx}:${esc(o.id)}">${esc(o.label)}</button>`)
            .join("") +
          `</span>`
        : "";
      parts.push(`<div class="cn-srcsum"><span class="cn-srcsum-chip${it.attempts.length ? "" : " unsearched"}">${inner}</span>${retry}${opts}</div>`);
    }
    else if (it.kind === "notice") parts.push(`<div class="cn-notice">${esc(it.text)}</div>`);
    else if (it.kind === "hd_tickets") parts.push(hdTicketsHtml(it));
    else if (it.kind === "hd_ticket") parts.push(hdThreadHtml(it));
    // MCP-Apps card: only the placeholder slot goes through the innerHTML flow — the live
    // iframe sits in the persistent .cn-cards layer, pinned over this slot by syncMcpCards().
    else if (it.kind === "mcp_app") parts.push(`<div class="cn-mcp-slot" data-mcp-slot="${it.key}" style="height:${mcpCards.get(it.key)?.height ?? 560}px"></div>`);
    else if (it.kind === "hd_kb") parts.push(hdKbHtml(it, idx));
    else if (it.kind === "choices") parts.push(`<div class="cn-choices">${it.prompt ? `<div class="cn-hint" style="text-align:left;margin:0 0 4px">${esc(it.prompt)}</div>` : ""}<div class="hd-acts">${it.options.map((o) => o.scan
      ? `<button class="cn-sample hd-mini" data-scan-subject="${esc(o.scan.subject)}"${o.info ? ` title="${esc(o.info)}"` : ""}>${esc(o.label)}</button>`
      : `<button class="cn-sample hd-mini" data-prompt="${esc(o.handoff ? o.handoff.prompt : (o.ask || o.label))}"${o.handoff ? ` data-handoff-mode="${esc(o.handoff.mode)}"` : ""}${o.pinMode ? ` data-pin-mode="${esc(o.pinMode)}"` : ""}${o.info ? ` title="${esc(o.info)}"` : ""}>${esc(o.label)}</button>`).join("")}</div></div>`);
    else if (it.kind === "scanpick") parts.push(scanPickHtml(it, idx));
    else if (it.kind === "confirm") parts.push(confirmHtml(it, idx));
    else {
      const again = it.again && !it.againFired && !busy
        ? `<button class="cn-again" data-again="${idx}">↻ Try again</button>`
        : "";
      parts.push(`<div class="cn-error">${esc(it.text)}${again}</div>`);
    }
  });
  if (busy) parts.push(`<div class="cn-typing"><span></span><span></span><span></span></div>`);
  // Vault included (hang review scenario 13): the mode likeliest to run long was the one without
  // an escape hatch — safe now that the async endpoint accepts mode "vault".
  if (busy && (mode === "chat" || mode === "vault" || isModuleMode(mode))) parts.push(`<div class="cn-mailwhen"><button class="cn-act" id="cnMailWhen" title="Stop waiting — I'll finish in the background and email you">✉ Mail me when ready</button></div>`);
  // ‼️ The rebuilt HTML goes into .cn-body, NOT the thread itself: a bare thread.innerHTML
  // write would destroy the persistent .cn-cards layer and reload every card iframe.
  const layers = mcpLayers(thread);
  layers.body.innerHTML = parts.join("");
  syncMcpCards(thread);
  // Focus the inline steer box when it's open (it was just (re)created by innerHTML).
  if (expertOpenIdx !== null) { const s = $("#cnSteerIn") as HTMLInputElement | null; if (s && document.activeElement !== s) { s.focus(); s.setSelectionRange(s.value.length, s.value.length); } }
  if (nearBottom) thread.scrollTop = thread.scrollHeight;
  const clear = $("#cnClear");
  if (clear) clear.style.display = items.length ? "" : "none";
  // read-only badge only for data-grounded modes (Chat is web, not read-only data)
  const ro = $<HTMLElement>(".cn-ro");
  if (ro) ro.style.display = isDataMode(mode) ? "" : "none";
  // mode-aware composer placeholder
  const ta = $("#cnInput") as HTMLTextAreaElement | null;
  if (ta) ta.placeholder = mode === "chat" ? "Ask anything…" : mode === "faq" ? "Ask your company & personal FAQ…" : isModuleMode(mode) ? `Ask about your ${contentModes().find((m) => m.id === mode)?.label.toLowerCase() || mode}…` : "Ask about your business data…";
  renderModes();
  syncSend();
}

// ── SAVE-CANON D2: USER recipes on the phone (closes "mobile = hardcoded starters") ────────────
// The ⚡ popover now lists the user's own recipes for the mode (run on tap, ✎ to edit) above the
// static starters, plus "+ New recipe". The editor is a bottom sheet: explicit Save + the shared
// Discard guard (new-record canon); schedule/cap/tidy live upstream via the /api/recipes proxy.
// ‼️ `options` is carried, not rebuilt. The phone used to save `options: {}`, which silently wiped
// Deep thinking / connector scoping / Web sources off a recipe the moment you edited its title here.
type RecipeOptions = { deepThinking?: boolean; askExpert?: boolean; connectorIds?: number[]; searchFocus?: "news" | "papers" | "both" };
type UserRecipe = { id: number; mode: string; title: string; prompt: string; options?: RecipeOptions; schedule: { freq: string; hour: number; minute?: number; weekday?: number; ordinal?: string } | null; active: boolean };
let userRecipes: UserRecipe[] = [];
let userRecipesLoaded = false;
async function fetchUserRecipes(force = false): Promise<void> {
  if (userRecipesLoaded && !force) return;
  try {
    const r = await fetch("/api/recipes", { credentials: "include" });
    const j = (await r.json().catch(() => ({}))) as { recipes?: UserRecipe[] };
    if (r.ok && Array.isArray(j.recipes)) { userRecipes = j.recipes; userRecipesLoaded = true; }
  } catch { /* starters still render */ }
}
let rcpEditId: number | null = null;
let rcpOpts: RecipeOptions = {};        // the loaded recipe's options, carried through on save
let rcpFocus: "news" | "papers" | "both" = "news";
let rcpSnap = "";
const FOCUS_HINT: Record<"news" | "papers" | "both", string> = {
  news: "News and trade press.",
  both: "Both lanes — one extra search per topic.",
  papers: "Peer-reviewed work only.",
};
/** Paint the segment + its hint. Chat-only: any other topic answers from your own data. */
function rcpSyncFocus(): void {
  const field = $("#rcpFocusField") as HTMLElement | null;
  field?.toggleAttribute("hidden", mode !== "chat");
  document.querySelectorAll<HTMLElement>("#rcpFocus [data-focus]").forEach((b) =>
    b.classList.toggle("on", b.getAttribute("data-focus") === rcpFocus));
  const hint = $("#rcpFocusHint"); if (hint) hint.textContent = FOCUS_HINT[rcpFocus];
}
let rcpFreq: { getValue: () => string } | null = null;
let rcpDay: { getValue: () => string } | null = null;
let rcpOrd: { getValue: () => string } | null = null;
const rcpNow = (): string => JSON.stringify({
  title: ($("#rcpTitle") as HTMLInputElement | null)?.value.trim() || "",
  prompt: ($("#rcpPrompt") as HTMLTextAreaElement | null)?.value.trim() || "",
  focus: rcpFocus,
  freq: rcpFreq?.getValue() || "off", time: ($("#rcpTime") as HTMLInputElement | null)?.value || "",
  day: rcpDay?.getValue() || "1", ord: rcpOrd?.getValue() || "first",
});
function rcpSyncSchedule(): void {
  const f = rcpFreq?.getValue() || "off";
  ($("#rcpTimeField") as HTMLElement | null)?.toggleAttribute("hidden", f === "off");
  ($("#rcpDayField") as HTMLElement | null)?.toggleAttribute("hidden", f !== "weekly");
  ($("#rcpOrdField") as HTMLElement | null)?.toggleAttribute("hidden", f !== "monthly");
}
function openRecipeEditor(r: UserRecipe | null): void {
  rcpEditId = r ? r.id : null;
  const hd = $("#rcpTitleHd"); if (hd) hd.textContent = r ? "Recipe" : "New recipe";
  ($("#rcpTitle") as HTMLInputElement | null)!.value = r?.title || "";
  ($("#rcpPrompt") as HTMLTextAreaElement | null)!.value = r?.prompt || "";
  ($("#rcpTime") as HTMLInputElement | null)!.value = r?.schedule ? `${String(r.schedule.hour).padStart(2, "0")}:${String(r.schedule.minute ?? 0).padStart(2, "0")}` : "07:00";
  const del = $("#rcpDelete") as HTMLElement | null; if (del) del.hidden = !r;
  rcpOpts = r?.options ?? {};
  rcpFocus = rcpOpts.searchFocus ?? "news";
  rcpSyncFocus();
  const freqHost = $("#rcpFreqDD"); if (freqHost) { freqHost.innerHTML = ""; const dd = makeDropdown({ placeholder: "Off", value: r?.schedule?.freq || "off", options: [{ value: "off", label: "Off — run it manually" }, { value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }], onChange: rcpSyncSchedule }); freqHost.appendChild(dd.el); rcpFreq = dd; }
  const dayHost = $("#rcpDayDD"); if (dayHost) { dayHost.innerHTML = ""; const dd = makeDropdown({ placeholder: "Monday", value: String(r?.schedule?.weekday ?? 1), options: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((l, i) => ({ value: String(i + 1), label: l })) }); dayHost.appendChild(dd.el); rcpDay = dd; }
  const ordHost = $("#rcpOrdDD"); if (ordHost) { ordHost.innerHTML = ""; const dd = makeDropdown({ placeholder: "First", value: r?.schedule?.ordinal || "first", options: [{ value: "first", label: "First weekday of the month" }, { value: "last", label: "Last weekday of the month" }] }); ordHost.appendChild(dd.el); rcpOrd = dd; }
  rcpSyncSchedule();
  $("#rcpScrim")?.classList.add("open");
  $("#rcpSheet")?.classList.add("open");
  setTimeout(() => { rcpSnap = rcpNow(); }, 0);
}
function closeRecipeEditor(): void { $("#rcpScrim")?.classList.remove("open"); $("#rcpSheet")?.classList.remove("open"); rcpEditId = null; }
async function guardedCloseRecipe(): Promise<void> {
  if (rcpSnap && rcpNow() !== rcpSnap) { if (!(await confirmDiscard("this recipe"))) return; }
  closeRecipeEditor();
}
async function saveUserRecipe(): Promise<void> {
  const title = ($("#rcpTitle") as HTMLInputElement | null)?.value.trim() || "";
  const prompt = ($("#rcpPrompt") as HTMLTextAreaElement | null)?.value.trim() || "";
  if (!title || !prompt) { toast("Give the recipe a title and a prompt"); return; }
  const f = rcpFreq?.getValue() || "off";
  const tm = (($("#rcpTime") as HTMLInputElement | null)?.value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (f !== "off" && !tm) { toast("Time as HH:MM, e.g. 07:00"); return; }
  const schedule = f === "off" ? null : {
    freq: f, hour: Number(tm![1]), minute: Number(tm![2]),
    ...(f === "weekly" ? { weekday: Number(rcpDay?.getValue() || "1") } : {}),
    ...(f === "monthly" ? { weekday: Number(rcpDay?.getValue() || "1"), ordinal: rcpOrd?.getValue() || "first" } : {}),
  };
  try {
    const r = await fetch("/api/recipes", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({
        action: "save", ...(rcpEditId ? { id: rcpEditId } : {}), mode, title, prompt,
        // Carry what the desktop set (Deep thinking, connector scoping) and overlay only what this
        // sheet actually edits — the phone must never be the client that quietly resets a recipe.
        // (searchFocus is dropped first, so switching BACK to News really clears it.)
        options: { ...rcpOpts, searchFocus: undefined, ...(mode === "chat" && rcpFocus !== "news" ? { searchFocus: rcpFocus } : {}) },
        schedule, active: true,
      }),
    });
    const j = (await r.json().catch(() => ({}))) as { error?: string; limit?: number };
    if (!r.ok) { toast(j.error === "too_many_recipes" ? `Recipe limit reached (${j.limit ?? 10} per topic)` : "Couldn't save — try again"); return; }
    toast(rcpEditId ? "Recipe updated" : "Recipe added");
    closeRecipeEditor();
    await fetchUserRecipes(true);
    renderRecipeList();
  } catch { toast("Couldn't save — check your connection"); }
}
async function deleteUserRecipe(): Promise<void> {
  if (!rcpEditId) return;
  try {
    const r = await fetch("/api/recipes", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ action: "delete", id: rcpEditId }),
    });
    if (!r.ok) { toast("Couldn't delete — try again"); return; }
    toast("Recipe deleted");
    closeRecipeEditor();
    await fetchUserRecipes(true);
    renderRecipeList();
  } catch { toast("Couldn't delete — check your connection"); }
}

// recipes for the current mode: module/faq/helpdesk → static starters; vault → static starters
// (parity with the pane's VAULT_STARTERS — the ⚡ popover was empty on the phone in MailVault
// mode, hang review §3.2); CRM → connector recipes; Chat → none
const VAULT_STARTERS: Recipe[] = [
  { id: 1, title: "Follow-up radar", prompt: "What is on my follow-up radar?" },
  { id: 2, title: "Waiting on me", prompt: "Which incoming mails are still waiting for my reply?" },
  { id: 3, title: "Briefing for today", prompt: "Give me my meeting briefing for today." },
  { id: 4, title: "Organization dossier", prompt: "Give me the dossier of " },
];
/** M1.1: one starter per source the user has — the ⚡ "mixed suggestions" list under Auto
 *  (mirrors ConnectView.autoMixed). `pin` scopes the tap to that source. */
function autoMixedStarters(): { title: string; prompt: string; pin: string }[] {
  const out: { title: string; prompt: string; pin: string }[] = [];
  for (const mm of ["contacts", "tasks", "receipts", "minutes", "docs"] as const) {
    const r = MODULE_RECIPES[mm]?.[0];
    if (r) out.push({ title: `${routeLabel(mm)} · ${r.title}`, prompt: r.prompt, pin: mm });
  }
  if (quotesOn && MODULE_RECIPES.quotes?.[0]) out.push({ title: `Quotes · ${MODULE_RECIPES.quotes[0].title}`, prompt: MODULE_RECIPES.quotes[0].prompt, pin: "quotes" });
  const crmRec = hasCrm() && meta ? meta.connectors.filter((c) => connectorModeOf(c.type) === "crm").flatMap((c) => c.recipes)[0] : null;
  if (crmRec) out.push({ title: `${routeLabel("crm")} · ${crmRec.title}`, prompt: crmRec.prompt, pin: "crm" });
  if (hasVault() && VAULT_STARTERS[0]) out.push({ title: `${routeLabel("vault")} · ${VAULT_STARTERS[0].title}`, prompt: VAULT_STARTERS[0].prompt, pin: "vault" });
  return out;
}
function recipesForMode(): Recipe[] {
  if (isModuleMode(mode) || mode === "helpdesk") return MODULE_RECIPES[mode] || [];
  if (mode === "vault") return VAULT_STARTERS;
  if (mode === "crm" && meta) return meta.connectors.find((c) => c.id === activeId)?.recipes ?? meta.connectors.flatMap((c) => c.recipes);
  return [];
}

// ── Helpdesk snapshot card (HELPDESK-TICKETING-SPEC §3.5) — one cheap fetch when the tab opens
//    (no LLM tokens); row actions are plain data-prompt buttons, so the existing sample
//    delegation sends them as chat turns. Mirrors the pane's .hd-snap card.
type HdTicket = { id: number; ref: string; subject: string; customer: { name: string; email: string }; stage: string; updatedAt: string };
let hdSnap: { tickets: HdTicket[]; team: string | null } | "error" | null = null;
let hdState: "open" | "closed" = "open"; // card filter chips (Gery 2026-07-06)
function hdAgo(ts: string): string {
  const t = new Date(/[Z+]/.test(ts) ? ts : `${ts}Z`).getTime();
  if (!Number.isFinite(t)) return "";
  const m = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
async function loadHdSnap(): Promise<void> {
  hdSnap = null;
  try {
    const r = await fetch(`/api/connect/helpdesk-tickets${hdState === "closed" ? "?state=closed" : ""}`, { credentials: "include" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = (await r.json()) as { tickets?: HdTicket[]; team?: string | null };
    hdSnap = { tickets: Array.isArray(d.tickets) ? d.tickets : [], team: d.team ?? null };
  } catch { hdSnap = "error"; }
  if (mode === "helpdesk") render();
}
function hdChipsHtml(): string {
  return `<div class="hd-chips"><button class="${hdState === "open" ? "on" : ""}" data-hd-state="open">Open</button><button class="${hdState === "closed" ? "on" : ""}" data-hd-state="closed">Resolved / closed</button></div>`;
}
function hdSnapHtml(): string {
  if (hdSnap === "error") return `<p class="cn-hint">Couldn't load the ticket list — ask me directly, e.g. "show open tickets".</p>`;
  if (!hdSnap) return skeletonHtml(3);
  if (!hdSnap.tickets.length) return `<div class="hd-snap"><div class="hd-snap-hd"><span>${hdState === "closed" ? "Resolved / closed" : "Open tickets"}${hdSnap.team ? ` — ${esc(hdSnap.team)}` : ""}</span><span class="hd-snap-n">0</span></div>${hdChipsHtml()}<div class="hd-kb-a" style="margin-top:6px">${hdState === "closed" ? "No resolved tickets yet." : "🎉 No open tickets right now."}</div></div>`;
  const rows = hdSnap.tickets.map((t) => {
    const who = t.customer.name || t.customer.email || "unknown customer";
    return `<div class="hd-row"><div class="hd-main">` +
      `<div class="hd-ref">${esc(t.ref)} · ${esc(hdAgo(t.updatedAt))}</div>` +
      `<div class="hd-sub">${esc(t.subject)}</div>` +
      `<div class="hd-who">${esc(who)}</div>` +
      `<div class="hd-acts"><button class="cn-sample hd-mini" data-prompt="Show me ticket ${esc(t.ref)} (id ${t.id}) in full.">Open</button>` +
      `<button class="cn-sample hd-mini" data-prompt="Read ticket ${esc(t.ref)} (id ${t.id}) and draft a reply to the customer.">Draft reply</button>` +
      `<button class="cn-sample hd-mini" data-prompt="Propose closing ticket ${esc(t.ref)} (id ${t.id}) as resolved.">Close</button>` +
      `<button class="cn-sample hd-mini bad" data-prompt="Propose deleting ticket ${esc(t.ref)} (id ${t.id}) as spam.">Delete</button></div>` +
      `</div><span class="hd-stage">${esc(t.stage)}</span></div>`;
  }).join("");
  return `<div class="hd-snap"><div class="hd-snap-hd"><span>${hdState === "closed" ? "Resolved / closed" : "Open tickets"}${hdSnap.team ? ` — ${esc(hdSnap.team)}` : ""}</span><span class="hd-snap-n">${hdSnap.tickets.length}${hdSnap.tickets.length >= 10 ? "+" : ""}</span></div>${hdChipsHtml()}${rows}<button class="cn-sample hd-all" data-prompt="${hdState === "closed" ? "Show all resolved and closed tickets." : "Show all open tickets."}">Show all</button></div>`;
}
// M1 (spec §3.2): the mode-balloon row is gone (it duplicated the chip set). A new chat under
// Auto opens with the CARDS — per-source starters carrying a source tag; a card is a launcher
// that pre-picks its source for the first turn (requested_scope). Explicit sources keep their
// hint + starter samples.
function modeBalloonsHtml(): string {
  if (!autoOn() || recipesOpen) return "";
  // M1.1: the user's SAVED recipes lead the grid, scheduled (⏰) first (spec §3.2 — parity with
  // the PWA cards). fetchUserRecipes is kicked off by loadConnect; a late arrival re-renders.
  const mine = [...userRecipes].sort((a, b) => Number(!!b.active) - Number(!!a.active)).slice(0, 4);
  const starters: { tag: string; m: string; title: string; prompt: string }[] = (["contacts", "tasks", "receipts", "docs"] as const)
    .map((mm) => ({ tag: routeLabel(mm), m: mm, ...(MODULE_RECIPES[mm]?.[0] ?? { title: "", prompt: "" }) }))
    .filter((s) => s.title);
  // ‼️ only connectors that FEED the CRM pill (connectorModeOf) — a sendcloud/helpdesk/vault
  // recipe must never surface under a CRM-labeled card (review 2026-08-18)
  const crmRec = hasCrm() && meta ? meta.connectors.filter((c) => connectorModeOf(c.type) === "crm").flatMap((c) => c.recipes)[0] : null;
  const card = (tag: string, title: string, prompt: string, pin: string): string =>
    `<button class="connect-card" data-card-prompt="${esc(prompt)}" data-card-mode="${esc(pin)}" title="${esc(prompt)}"><span class="cc-tag">${esc(tag)}</span><span class="cc-title">${esc(title)}</span></button>`;
  return `<div class="connect-cards">` +
    mine.map((r) => card(r.active ? "⏰ Scheduled" : routeLabel(r.mode) || "Saved", r.title, r.prompt, r.mode !== "chat" ? r.mode : "")).join("") +
    starters.map((s) => card(s.tag, s.title, s.prompt, s.m)).join("") +
    (crmRec ? card(routeLabel("crm"), crmRec.title, crmRec.prompt, "crm") : "") +
    `</div>`;
}
function emptyHintHtml(): string {
  if (autoOn()) return `<p class="cn-hint">Ask anything — Auto picks the right source for each question.</p>`;
  if (mode === "faq") return `<p class="cn-hint">Ask your knowledge base — your own FAQ plus your company's shared FAQ and documents.</p>`;
  if (isModuleMode(mode)) return `<p class="cn-hint">Ask about your ${esc(contentModes().find((m) => m.id === mode)?.label.toLowerCase() || mode)} — answers come straight from your own data.</p>`;
  if (mode === "crm" && meta) return `<p class="cn-hint">Ask anything about your business data — answers come straight from ${esc(meta.connectors.map((c) => c.label).join(" · "))}.</p>`;
  if (mode === "helpdesk") return `<p class="cn-hint">Your support team's tickets — I read the thread, search the helpdesk knowledge base and draft the reply. You stay in control.</p>`;
  return `<p class="cn-hint">Ask anything — web search is on.</p>`;
}
function samplesHtml(): string {
  if (recipesOpen) return ""; // ⚡ popover shows the starters — don't also show them as balloons
  return recipesForMode().slice(0, 3).map((s) => `<button class="cn-sample" data-prompt="${esc(s.prompt)}">${esc(s.title)}</button>`).join("");
}
// Deep-thinking trace — collapsible "Thinking…" panel above the answer (mirrors ConnectView.ThinkingPanel).
function thinkPanelHtml(it: Extract<Item, { kind: "thinking" }>, idx: number): string {
  const label = it.done ? (it.ms ? `Thought for ${Math.max(1, Math.round(it.ms / 1000))}s` : "Thoughts") : "Thinking…";
  return `<div class="cn-think${it.done ? " done" : ""}">` +
    `<button class="cn-think-h" data-think="${idx}">${it.done ? "" : '<span class="cn-think-dot"></span>'}<span class="cn-think-lb">${esc(label)}</span><span class="cn-think-cv">${it.open ? "▾" : "▸"}</span></button>` +
    (it.open && it.text ? `<div class="cn-think-body">${esc(it.text)}</div>` : "") +
  `</div>`;
}
// ── Meeting Scan / Deep Scan (MEETING-SCAN-BRAINSTORM, Gery 2026-08-18) — companion port. Same
// rules as the pane: Meeting Scan renders ONLY with a Vault connector; Deep Scan is universal;
// mail-only v1 (the dossier lands in the paired user's own inbox via the proxy's identity).
const SCAN_QUEUED = (label: string): string =>
  `📡 Scan started — ${label}. The dossier lands in your inbox in a few minutes. You can close this screen or keep chatting.`;
async function scanApi(body: Record<string, unknown>): Promise<{ date?: string; meetings?: ScanMeeting[] | null; skipped?: string }> {
  const r = await fetch("/api/connect/meeting-scan", {
    method: "POST", credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const j = (await r.json().catch(() => ({}))) as { error?: string; date?: string; meetings?: ScanMeeting[] | null; skipped?: string };
  if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
  return j;
}
function fireScan(payload: Record<string, unknown>, label: string): void {
  items.push({ kind: "notice", text: SCAN_QUEUED(label) });
  render();
  void scanApi({ action: "scan", ...payload }).catch((e) => {
    items.push({ kind: "error", text: String((e as Error)?.message || e) });
    render();
  });
}
function openMeetingScan(when: "today" | "tomorrow", card?: Extract<Item, { kind: "scanpick" }>): void {
  recipesOpen = false;
  if (card) { card.when = when; card.loading = true; card.meetings = null; card.error = undefined; }
  else { card = { kind: "scanpick", when, meetings: null, loading: true }; items.push(card); }
  render();
  void scanApi({ action: "meetings", when })
    .then((r) => {
      card!.loading = false;
      if (r.meetings == null) card!.error = "Your calendar isn't reachable right now — try again, or use Deep Scan with a company name.";
      else { card!.meetings = r.meetings; card!.date = r.date; }
      render();
    })
    .catch((e) => { card!.loading = false; card!.error = String((e as Error)?.message || e); render(); });
}
function scansHtml(): string {
  if (!meta) return "";
  const hasVault = meta.connectors.some((c) => c.type === "mailvault");
  return `<div class="chat-opts"><div class="chat-opts-h">Scans</div>` +
    (hasVault
      ? `<div class="chat-opt-row" data-opt="scan-meetings"><div class="co-l"><div class="co-name">📡 Meeting Scan</div><div class="co-sub">Pick a meeting from your calendar — full dossier, mailed · ~€0.10</div></div></div>`
      : "") +
    `<div class="chat-opt-row" data-opt="scan-deep"><div class="co-l"><div class="co-name">🔍 Deep Scan</div><div class="co-sub">Pick a company or contact — no calendar needed, mailed · ~€0.10</div></div></div>` +
  `</div>`;
}
function scanPickHtml(it: Extract<Item, { kind: "scanpick" }>, idx: number): string {
  if (it.loading) return `<div class="cn-hint" style="text-align:left">📡 Reading your calendar…</div>`;
  if (it.error) return `<div class="cn-error">${esc(it.error)}</div>`;
  const chips = (it.meetings || []).map((m, k) =>
    `<button class="cn-sample hd-mini${it.picked === m.title ? " on" : ""}" data-scan-pick="${idx}:${k}"${it.picked ? " disabled" : ""}>${esc(m.time ? `${m.time} · ${m.title}` : m.title)}</button>`).join("");
  const none = (it.meetings || []).length === 0 ? `<div class="cn-hint" style="text-align:left">No meetings in your calendar for ${it.when}.</div>` : "";
  const day = it.picked ? "" : `<button class="cn-sample hd-mini" data-scan-day="${idx}">${it.when === "today" ? "Tomorrow ›" : "‹ Today"}</button>`;
  const all = !it.picked && (it.meetings || []).length > 1 ? `<button class="cn-sample hd-mini" data-scan-all="${idx}">All of ${it.when}</button>` : "";
  return `<div class="cn-choices"><div class="cn-hint" style="text-align:left;margin:0 0 4px">📡 Which meeting should I scan? (${it.when}${it.date ? ` · ${esc(it.date)}` : ""})</div><div class="hd-acts">${chips}${day}${all}</div>${none}</div>`;
}

// ⚡ "Chat options" — Deep thinking + Mail me toggles (Chat mode; render into the recipes popover).
function chatOptsHtml(): string {
  return `<div class="chat-opts">` +
    `<div class="chat-opts-h">Chat options</div>` +
    `<div class="chat-opt-row" data-opt="deep"><div class="co-l"><div class="co-name">Deep thinking</div><div class="co-sub">Strongest model · shows its thinking · costs more</div></div><span class="cn-switch${deepThinking ? " on" : ""}"></span></div>` +
    `<div class="chat-opt-row" data-opt="mail"><div class="co-l"><div class="co-name">Mail me the result</div><div class="co-sub">Ask &amp; walk away — the answer arrives by email</div></div><span class="cn-switch${mailMeOn ? " on" : ""}"></span></div>` +
  `</div>`;
}
// ⚡ "Ask Expert" options (CRM mode): the pre-arm toggle + Mail me the result. The per-answer steer is
// NOT here — it opens inline at each answer's Ask Expert button (it changes per question).
function crmOptsHtml(): string {
  return `<div class="chat-opts">` +
    `<div class="chat-opts-h">Ask Expert</div>` +
    `<div class="chat-opt-row" data-opt="arm"><div class="co-l"><div class="co-name">Ask Expert (deep analysis)</div><div class="co-sub">Run the deep analyst pass on every answer · slower · more credits</div></div><span class="cn-switch${askExpertArmed ? " on" : ""}"></span></div>` +
    `<div class="chat-opt-row" data-opt="mail"><div class="co-l"><div class="co-name">Mail me the result</div><div class="co-sub">Ask &amp; walk away — the answer arrives by email</div></div><span class="cn-switch${mailMeOn ? " on" : ""}"></span></div>` +
  `</div>`;
}
// M0 (BRAIN-AUTO-MODE-SPEC v2 §3.1): switching mode KEEPS the conversation — source choice is
// per turn now (mirrors ConnectView.pickMode). Only a running turn is aborted; per-answer
// action state resets, the thread stays.
function pickMode(m: BrainMode): void {
  if (m === mode) { recipesOpen = false; render(); return; }
  abortCtrl?.abort();
  abortCtrl = null;
  pendingVoice = null;
  busy = false;
  thinkingStop();
  mailState = "idle";
  xlsState = "idle";
  lastC360 = null;
  expertOpenIdx = null;
  expertSteer = "";
  expertRunningFrom = null;
  scanAsk = false;
  currentTurn = null;
  mailHandoff = false;
  mode = m;
  activeId = null;
  recipesOpen = false;
  if (m === "helpdesk") void loadHdSnap(); // one cheap fetch on tab open (spec §3.5)
  render();
}

// The Brain mode bar (replaces the old connector chips): a fixed [? ⚡ │] utility cluster +
// the content modes scrolling horizontally beside it — ONE source, mirrors ConnectView's row.
function renderModes(): void {
  const wrap = $("#cnChips");
  if (!wrap || !meta) return;
  const recs = recipesForMode();
  const hasOpts = recs.length > 0 || mode === "chat" || mode === "crm"; // ⚡ opens options in Chat + CRM + module modes
  wrap.className = "cn-chips cn-modes";
  // M1 (spec §3.1): composer utilities = [✦ Auto ▾] [⚡] — the "?" is gone (Company knowledge
  // lives in the selector popover) and the scrolling pill row is replaced by the selector.
  wrap.innerHTML =
    `<div class="brain-selwrap">` +
      `<button class="brain-selector${autoOn() ? " auto" : ""}" data-seltoggle aria-haspopup="listbox" aria-expanded="${selOpen}" title="Choose where this question is answered from">${esc(selectorLabel())} <span class="brain-sel-car">▾</span></button>` +
      (selOpen ? selectorPopHtml() : "") +
    `</div>` +
    `<button class="brain-mode zap${recipesOpen ? " on" : ""}${deepThinking || (mode === "crm" && askExpertArmed) ? " lit" : ""}"${hasOpts ? "" : " disabled"} data-zap title="${mode === "chat" || mode === "crm" || isModuleMode(mode) ? "Chat options" : recs.length ? "Starter prompts" : "No starters for this mode"}" aria-label="${mode === "chat" || mode === "crm" || isModuleMode(mode) ? "Chat options" : "Starter prompts"}">⚡</button>`;
  const rec = $("#cnRecipes");
  if (rec) {
    if (recipesOpen && hasOpts) { rec.style.display = ""; renderRecipeList(); }
    else rec.style.display = "none";
  }
}

let recipesQ = "";
function renderRecipeList(): void {
  const listEl = $("#cnRecipeList");
  if (!listEl) return;
  const searchEl = $("#cnRecipeSearch") as HTMLElement | null;
  // Chat mode: the ⚡ popover hosts the Deep-thinking + Mail-me toggles instead of recipes —
  // plus the Scans section (Meeting Scan Vault-gated · Deep Scan universal), pane parity.
  if (mode === "chat") {
    if (searchEl) searchEl.style.display = "none";
    // M1.1 (spec §3.1 "Auto → mixed suggestions"): under Auto the popover also lists ONE starter
    // per source, so the sources stay discoverable once the empty-state cards are gone. Each
    // carries data-pin-mode so the tap runs scoped, not as a generic chat turn.
    const mixed = autoOn()
      ? `<div class="cn-rmore" style="margin-top:6px">Suggestions</div>` +
        autoMixedStarters().map((s) => `<button class="cn-recipe" data-prompt="${esc(s.prompt)}"${s.pin ? ` data-pin-mode="${esc(s.pin)}"` : ""}>${esc(s.title)}</button>`).join("")
      : "";
    listEl.innerHTML = chatOptsHtml() + scansHtml() + mixed;
    return;
  }
  if (searchEl) searchEl.style.display = "";
  const all = recipesForMode();
  const q = recipesQ.trim().toLowerCase();
  const hits = all.filter((r) => !q || r.title.toLowerCase().includes(q) || r.prompt.toLowerCase().includes(q));
  // SAVE-CANON D2: the user's OWN recipes first (tap = run, ✎ = edit), then "+ New", then starters.
  if (!userRecipesLoaded) void fetchUserRecipes().then(() => { if (recipesOpen) renderRecipeList(); });
  const mine = userRecipes.filter((r) => r.mode === mode && (!q || r.title.toLowerCase().includes(q) || r.prompt.toLowerCase().includes(q)));
  listEl.innerHTML =
    (mode === "crm" ? crmOptsHtml() : isModuleMode(mode) ? chatOptsHtml() : mode === "vault" ? scansHtml() : "") +
    mine.map((r) => `<div class="cn-recipe-row"><button class="cn-recipe" data-prompt="${esc(r.prompt)}">${esc(r.title)}${r.schedule && r.active ? " ⏰" : ""}</button><button class="cn-redit" data-redit="${r.id}" title="Edit recipe"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg></button></div>`).join("") +
    `<button class="cn-rnew" data-rnew="1">+ New recipe</button>` +
    hits.slice(0, 5).map((r) => `<button class="cn-recipe" data-prompt="${esc(r.prompt)}">${esc(r.title)}</button>`).join("") +
    (hits.length > 5 ? `<div class="cn-rmore">+${hits.length - 5} more — type to search</div>` : "") +
    (hits.length === 0 && mine.length === 0 ? `<div class="cn-rmore">No matches</div>` : "");
}

function syncSend(): void {
  const btn = $("#cnSend");
  const input = $("#cnInput") as HTMLTextAreaElement | null;
  if (!btn || !input) return;
  btn.classList.toggle("stop", busy);
  (btn as HTMLButtonElement).disabled = !busy && !input.value.trim();
}

// ── chat turn (SSE through our proxy) ──
let pendingVoice: string | null = null;
function flushPendingVoice(): void {
  const t = pendingVoice;
  if (!t) return;
  pendingVoice = null;
  void send(t);
}

async function send(text: string, opts?: { askExpert?: boolean; refine?: string; pinMode?: string; correctedFrom?: string; replayUser?: boolean; sourceRetry?: boolean }): Promise<void> {
  const q = text.trim();
  if (!q || busy || !meta) return;
  // Deep Scan subject prompt: the next send IS the scan subject, never a chat turn.
  if (scanAsk) {
    scanAsk = false;
    const input0 = $("#cnInput") as HTMLTextAreaElement | null;
    if (input0) input0.value = "";
    items.push({ kind: "user", text: `Deep Scan: ${q}` });
    fireScan({ subject: q }, q);
    return;
  }
  // `pinMode`: an offer chip emitted BY a module's tool must land back in that module, whatever
  // mode the chat itself is in (desktop parity — there an auto-routed Chat swallowed it).
  const effPin = opts?.pinMode ?? (VAULT_CHIP_RE.test(q) && vaultIds().length ? "vault" : undefined);
  // ‼️ A tapped Export/Mail chip is an ACTION, not a question (Gery 2026-08-20): pinning the source
  // was not enough — with tool_choice "auto" the model answered the second Export tap from the
  // conversation and claimed success while the backend never saw a call. Actions force the tool.
  const forceTool = !!opts?.sourceRetry || VAULT_CHIP_RE.test(q);
  const m: BrainMode = (effPin as BrainMode | undefined) ?? mode;
  // Deep CRM = the per-answer Ask Expert button OR the pre-arm toggle (every answer deep).
  const askExpertFlag = mode === "crm" && (!!opts?.askExpert || askExpertArmed);
  const refine = mode === "crm" ? (opts?.refine?.trim() || undefined) : undefined; // steer (CRM deep only)
  plopSend();
  const input = $("#cnInput") as HTMLTextAreaElement | null;
  if (input) input.value = "";
  recipesOpen = false;
  busy = true;
  mailState = "idle";
  xlsState = "idle";
  lastC360 = null;
  const history = items
    .filter((i): i is Extract<Item, { kind: "user" | "assistant" }> => i.kind === "user" || i.kind === "assistant")
    .map((i) => ({ role: i.kind === "user" ? "user" : "assistant", content: i.text }));

  // "Mail me the result" pre-picked in the ⚡ menu (Chat · CRM · module modes): run it server-side and
  // email the answer — don't stream on-screen. The chat stays open to ask the next thing.
  // ‼️ Pane parity (review 2026-08-18, never ported here): a retry / ✕-correction / pinned chip tap
  // is interactive BY DEFINITION. An armed Mail-me used to swallow those too and queue a mail
  // instead — "Try another source" then looked like it did nothing at all, which is exactly the
  // symptom under investigation. Never hijack a correction or a pinned turn.
  const isCorrection = forceTool || !!opts?.correctedFrom;
  if (mailMeOn && !isCorrection && !effPin && (mode === "chat" || mode === "crm" || mode === "vault" || isModuleMode(mode))) {
    mailMeOn = false; // per-request
    busy = false;     // not a streaming turn
    items.push({ kind: "user", text: q });
    items.push({ kind: "notice", text: MAIL_QUEUED });
    render();
    const msgs = [...history, { role: "user", content: q }];
    const p = mode === "crm"
      ? mailChatAsync(msgs, { mode: "crm", connectorIds: activeId ? [activeId] : (crmIds().length ? crmIds() : undefined), askExpert: askExpertFlag, refine })
      : mode === "vault"
        ? mailChatAsync(msgs, { mode: "vault", connectorIds: vaultIds().length ? vaultIds() : undefined })
      : isModuleMode(mode)
        ? mailChatAsync(msgs, { mode })
        : mailChatAsync(msgs, { auto: autoOn(), prevRouted }); // M1.1: an armed Mail-me under Auto is routed server-side
    void p.catch((e) => { items.push({ kind: "error", text: String((e as Error)?.message || e) }); render(); });
    return;
  }

  // Ask Expert re-runs the SOURCE question; render the deep answer BENEATH the original answer rather
  // than re-showing the question as a new user bubble. A ✕-correction re-run keeps the original
  // bubble too (replayUser) — the retried answer lands under its badge (append, never erase).
  if (!askExpertFlag && !opts?.replayUser) items.push({ kind: "user", text: q });
  // ‼️ Where THIS turn begins — pane parity, and the whole correctness of the "was the answer
  // interrupted?" rule below. A re-run that pushes no user bubble (retry / ✕ / Ask Expert) must
  // not inherit the PREVIOUS turn's answer as proof that it had already started answering:
  // that turned every failure of a retry into "this answer is getting too long, I'll email it".
  const turnStart = items.length;
  thinkingStart();
  render();

  abortCtrl = new AbortController();
  // ── Hang-proofing (Docs/CONNECT-AUTH-HANG-REVIEW.md §4.2 — same deadlines as the pane/PWA):
  // no bytes for 30s (server heartbeats every 10s) or 65s total ⇒ the connection is dead; a wake
  // from phone-lock with a long-silent stream ⇒ stale. All become a visible error, never a spinner.
  const ctl = abortCtrl; // module var can be reassigned — the timers must abort THIS turn only
  let abortReason: "watchdog" | "stale" | null = null;
  let lastByteAt = Date.now();
  const absoluteT = setTimeout(() => { abortReason = "watchdog"; ctl.abort(); }, 65_000);
  let inactivityT: ReturnType<typeof setTimeout> | null = null;
  const armInactivity = (): void => {
    if (inactivityT) clearTimeout(inactivityT);
    inactivityT = setTimeout(() => { abortReason = "watchdog"; ctl.abort(); }, 30_000);
  };
  const onVisibility = (): void => {
    if (document.visibilityState === "visible" && Date.now() - lastByteAt > 70_000) { abortReason = "stale"; ctl.abort(); }
  };
  document.addEventListener("visibilitychange", onVisibility);
  const cleanupWatch = (): void => {
    clearTimeout(absoluteT);
    if (inactivityT) clearTimeout(inactivityT);
    document.removeEventListener("visibilitychange", onVisibility);
  };
  let turnId: string | null = null; // from the server's start event → the terminal report
  let routeNotified = false; // routed + route_selected carry the same info — report once
  let lastEvent: string | null = null;
  let doneSeen = false;
  // An offer card arrives with the TOOL result, i.e. before the answer streams — held here and
  // appended when the stream ends, so it reads as the next step instead of interrupting.
  let pendingChoices: { prompt: string | null; options: Extract<Item, { kind: "choices" }>["options"] } | null = null;
  // Pane parity (PR #16): once a vault card actually RENDERS this turn, the prose restating it
  // is dropped — the card is the answer. Only a card that really renders may silence the text;
  // a turn without one keeps the text as the answer, exactly as before.
  let cardShown = false;
  const reportTerminal = (reason: string): void => {
    if (!turnId) return;
    void fetch("/api/connect/turn-report", {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ turn_id: turnId, reason, last_event: lastEvent }),
    }).catch(() => { /* best-effort telemetry */ });
  };
  // Remember this turn so "Mail me when ready" can re-run it in the background (Chat · CRM · module modes).
  // ‼️ Keyed on `m`, the EFFECTIVE mode of this turn, not on `mode`, the mode the chat happens to
  // sit in. The request below already goes out on `m`; the handoff did not, so a "Try another
  // source → Vault" or a pinned chip tap fired at the Vault but told the background worker to
  // re-run it as open Chat — the pane's own Audi-Q7 fix, never ported here. sourceRetry rides
  // along on every branch for the same reason: a backgrounded retry must still force a lookup.
  currentTurn =
    // M1.1: the handoff carries the routing flags, so a capped Auto turn is routed in the worker.
    m === "chat" ? { messages: [...history, { role: "user", content: q }], deepThinking, mode: "chat", auto: autoOn(), prevRouted, ...(forceTool ? { sourceRetry: true } : {}) }
      : m === "crm" ? { messages: [...history, { role: "user", content: q }], mode: "crm", connectorIds: activeId ? [activeId] : (crmIds().length ? crmIds() : undefined), askExpert: askExpertFlag, refine }
      : m === "vault" ? { messages: [...history, { role: "user", content: q }], mode: "vault" as BrainMode, connectorIds: vaultIds().length ? vaultIds() : undefined, ...(forceTool ? { sourceRetry: true } : {}) }
        : isModuleMode(m) ? { messages: [...history, { role: "user", content: q }], deepThinking, mode: m, ...(forceTool ? { sourceRetry: true } : {}) }
          : null;
  let cur: Extract<Item, { kind: "assistant" }> | null = null;
  let curThink: Extract<Item, { kind: "thinking" }> | null = null;
  let thinkAt = 0;
  // Smooth typewriter: buffer deltas into `target`, reveal a little per animation frame straight into
  // the AI bubble's DOM (no full re-render per frame) so it reads like fast, fluid typing.
  let target = "";
  let shown = 0;
  let raf = 0;
  const aiEl = (): HTMLElement | undefined => {
    const list = ($("#cnThread") as HTMLElement | null)?.querySelectorAll(".cn-msg.ai");
    return list && list.length ? (list[list.length - 1] as HTMLElement) : undefined;
  };
  const stopType = (): void => { if (raf) { cancelAnimationFrame(raf); raf = 0; } };
  const flushType = (): void => { stopType(); if (cur && shown < target.length) { shown = target.length; cur.text = target; const el = aiEl(); if (el) el.innerHTML = answerHtml(cur.text); } };
  const tick = (): void => {
    raf = 0;
    if (cur && shown < target.length) {
      const gap = target.length - shown;
      shown = Math.min(target.length, shown + Math.max(3, Math.ceil(gap / 5)));
      cur.text = target.slice(0, shown);
      const el = aiEl(); if (el) el.innerHTML = answerHtml(cur.text);
      const thread = $("#cnThread"); if (thread && nearBottom) thread.scrollTop = thread.scrollHeight;
    }
    if (cur && shown < target.length) raf = requestAnimationFrame(tick);
  };
  const settle = () => {
    flushType();
    abortCtrl = null;
    busy = false;
    expertRunningFrom = null;
    thinkingStop();
    render();
    flushPendingVoice();
  };
  try {
    const res = await fetch("/api/connect/chat", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        surface: "companion", // telemetry only (brain_turns.surface)
        // M1.1: an explicit source pick ("Try another source") rides along so the server can
        // force a real lookup on round 1 (mirrors the pane's retryFlag).
        ...(forceTool ? { sourceRetry: true } : {}),
        ...(VAULT_CHIP_RE.test(q) ? { actionTurn: true } : {}),
        ...(m === "crm"
          ? { messages: [...history, { role: "user", content: q }], connectorIds: activeId ? [activeId] : (crmIds().length ? crmIds() : undefined), lang: navigator.language || "en", mode: "crm", askExpert: askExpertFlag, ...(refine ? { refine } : {}) }
          : m === "helpdesk"
            ? { messages: [...history, { role: "user", content: q }], connectorIds: helpdeskIds().length ? helpdeskIds() : undefined, lang: navigator.language || "en", mode: "helpdesk" }
            : m === "vault"
              ? { messages: [...history, { role: "user", content: q }], connectorIds: vaultIds().length ? vaultIds() : undefined, lang: navigator.language || "en", mode: "vault" }
          : m === "chat"
            ? {
                messages: [...history, { role: "user", content: q }], lang: navigator.language || "en", mode: "chat", deepThinking,
                // M1 auto-routing on mobile: offer exactly the pills this surface shows (+ faq);
                // sticky to the conversation's routed topic. A correction re-run reports the
                // rejected mode instead of re-routing.
                ...(autoOn() && !opts?.correctedFrom ? { auto: true, autoModes: [...contentModes().map((c) => c.id), "faq"], ...(prevRouted ? { prevRouted } : {}) } : {}),
                ...(opts?.correctedFrom ? { correctedFrom: opts.correctedFrom } : {}),
              }
            : { messages: [...history, { role: "user", content: q }], lang: navigator.language || "en", mode: m, deepThinking }), // own-data scoped chat / faq
      }),
      signal: abortCtrl.signal,
    });
    if (!res.ok || !res.body) {
      const data = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(data?.error || `HTTP ${res.status}`);
    }
    armInactivity();
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      lastByteAt = Date.now();
      armInactivity(); // any bytes — including the server's 10s heartbeats — reset the watchdog
      buf += dec.decode(value, { stream: true });
      const frames = buf.split("\n\n");
      buf = frames.pop() || "";
      for (const frame of frames) {
        const ev = frame.match(/^event:\s*(\S+)/m);
        const dat = frame.match(/^data:\s*(.*)$/m);
        if (!ev || !dat) continue;
        // `capped` rides on a notice when the server spent the turn's tool budget.
        let data: { text?: string; message?: string; connector?: string; tool?: string; state?: string; ms?: number; capped?: boolean } = {};
        try { data = JSON.parse(dat[1]); } catch { continue; }
        lastEvent = ev[1];
        if (ev[1] === "start") {
          const tid = (data as { turn_id?: string }).turn_id;
          turnId = typeof tid === "string" ? tid : null;
        } else if (ev[1] === "done") {
          // ‼️ Explicit done handling (hang review scenario 11): the phone used to clear busy only
          // on socket CLOSE — a buffered/held-open connection kept it spinning after a complete answer.
          doneSeen = true;
        } else if (ev[1] === "reasoning") {
          // Deep thinking: accumulate the reasoning trace into a collapsible "Thinking…" panel.
          if (!curThink) { curThink = { kind: "thinking", text: "", done: false, open: true }; thinkAt = Date.now(); items.push(curThink); }
          curThink.text += String(data.text || "");
          render();
        } else if (ev[1] === "delta") {
          if (cardShown) continue; // the card IS the answer — no prose restatement after it
          if (curThink && !curThink.done) { curThink.done = true; curThink.ms = Date.now() - thinkAt; curThink.open = false; } // answer started → collapse the trace
          if (!cur) { cur = { kind: "assistant", text: "", expert: askExpertFlag }; items.push(cur); target = ""; shown = 0; render(); } // create the empty bubble once
          target += String(data.text || "");
          if (!raf) raf = requestAnimationFrame(tick);
        } else if (ev[1] === "routed" || ev[1] === "route_selected") {
          // M1: the server routed this auto turn. Both events carry the same info (route_selected
          // is the v2 twin) — report once. Open-chat fallback = no chip, conversation unrouted.
          if (!routeNotified) {
            routeNotified = true;
            const rMode = String((data as { mode?: string; route?: string }).mode || (data as { route?: string }).route || "chat");
            prevRouted = rMode === "chat" ? null : rMode;
            // NOK-fix 2026-08-19: the background/mail handoff must follow the ROUTED source —
            // captured pre-routing as "chat", a capped routed turn re-ran as open web chat and
            // mailed a wrong answer (mirrors ConnectView.onRouted).
            if (rMode !== "chat" && currentTurn?.mode === "chat") {
              const msgs = currentTurn.messages;
              currentTurn =
                rMode === "crm" ? { messages: msgs, mode: "crm", connectorIds: activeId ? [activeId] : (crmIds().length ? crmIds() : undefined) }
                  : rMode === "vault" ? { messages: msgs, mode: "vault" as BrainMode, connectorIds: vaultIds().length ? vaultIds() : undefined }
                    : isModuleMode(rMode as BrainMode) ? { messages: msgs, deepThinking, mode: rMode as BrainMode }
                      : currentTurn;
            }
            if (rMode !== "chat") { flushType(); items.push({ kind: "routed", mode: rMode, q }); cur = null; render(); }
          }
        } else if (ev[1] === "truncated") {
          // NOK-fix 2026-08-19: an invisible cut answer looked like "no result" — say it.
          flushType();
          items.push({ kind: "notice", text: `⚠ The answer was cut short (${(data as { reason?: string }).reason === "repetition" ? "repetition guard" : "length limit"}). Ask again or narrow the question.` });
          cur = null;
          render();
        } else if (ev[1] === "source_summary") {
          // M0 attribution (spec §4): what this turn ACTUALLY consulted. Skip only the
          // nothing-to-say case (an open-chat turn where no lookup ran at all).
          const d = data as { route?: string; ms?: number; attempts?: { source?: string; tools?: number; status?: string }[] };
          const attempts = (Array.isArray(d.attempts) ? d.attempts : [])
            .map((a) => ({ source: String(a?.source || "?"), tools: Number(a?.tools) || 0, status: a?.status === "error" ? "error" : "searched" }))
            .filter((a) => a.tools > 0);
          const route = String(d.route || "chat");
          if (route !== "chat" || attempts.length) {
            flushType();
            // `ms` = the TURN's wall time, which the server has always sent and no client ever
            // showed — the tool strip only ever knew the sum of the tool calls.
            items.push({ kind: "srcsum", route, attempts, q, ms: typeof d.ms === "number" ? d.ms : undefined });
            cur = null;
            render();
          }
        } else if (ev[1] === "status") {
          flushType(); // finish typing the current bubble before a tool-status line lands
          if (data.state === "run") items.push({ kind: "status", connector: String(data.connector || ""), tool: String(data.tool || ""), state: "run" });
          else {
            const open = [...items].reverse().find((i) => i.kind === "status" && i.tool === data.tool && i.state === "run") as Extract<Item, { kind: "status" }> | undefined;
            if (open) { open.state = String(data.state || "done"); open.ms = data.ms; }
            else items.push({ kind: "status", connector: String(data.connector || ""), tool: String(data.tool || ""), state: String(data.state || "done"), ms: data.ms });
          }
          cur = null;
          render();
        } else if (ev[1] === "artifact") {
          // Structured tool payload (e.g. Customer 360). Capture it so "Mail me" can render the full
          // report server-side AND skip the slow Excel build — the cause of #001's mobile mail-fail.
          const k = (data as any)?.kind, d = (data as any)?.data;
          if (k === "c360" && d) lastC360 = d;
          if (k === "hd_tickets" && d) {
            flushType();
            items.push({ kind: "hd_tickets", team: (d as any).team ?? null, tickets: Array.isArray((d as any).tickets) ? (d as any).tickets : [] });
            cur = null;
            render();
          }
          if (k === "hd_ticket" && d) {
            flushType();
            items.push({ kind: "hd_ticket", ticket: d as any });
            cur = null;
            render();
          }
          if (k === "choices" && d) {
            const options = (Array.isArray((d as any).options) ? (d as any).options : [])
              .map((o: any) => (typeof o === "string" ? { label: o } : {
                label: String(o?.label || ""),
                info: o?.info ? String(o.info) : undefined,
                // `ask` carries the subject when the label is too short to stand alone;
                // `pinMode` keeps the turn in the module whose tool offered it.
                ask: o?.ask ? String(o.ask) : undefined,
                pinMode: o?.pin_mode ? String(o.pin_mode) : undefined,
                scan: o?.scan?.subject ? { subject: String(o.scan.subject).slice(0, 160) } : undefined,
                // A handoff option belongs to ANOTHER mode (the archive lives in Vault) — tapping
                // it re-opens Brain there with the question instead of sending its label into a
                // mode that has no tool for it.
                handoff: o?.handoff?.mode && o?.handoff?.prompt ? { mode: String(o.handoff.mode), prompt: String(o.handoff.prompt) } : undefined,
              }))
              .filter((o: any) => o.label).slice(0, 6);
            // Held back until the stream ends: an offer is the NEXT step, so it belongs under the
            // answer rather than between the tool chip and the text.
            if (options.length) pendingChoices = { prompt: (d as any).prompt ?? null, options };
          }
          // MCP-Apps card (SEP-1865): a vault tool result carrying its own server-rendered view.
          // The HTML arrives with the artifact; the persistent card host renders it in a
          // sandboxed iframe (see the card-host section). Only a payload that will actually
          // render flips cardShown — mirrors ConnectView.
          if (k === "mcp_app" && d && typeof (d as any).html === "string" && (d as any).html && Number.isFinite(Number((d as any).connector_id))) {
            flushType();
            cardShown = true;
            items.push({
              kind: "mcp_app",
              key: `m${++mcpSeq}`,
              app: {
                connector_id: Number((d as any).connector_id), tool: String((d as any).tool || ""),
                resource_uri: String((d as any).resource_uri || ""), html: String((d as any).html),
                content: Array.isArray((d as any).content) ? (d as any).content : [],
                structuredContent: (d as any).structuredContent ?? null, _meta: (d as any)._meta ?? null,
              },
            });
            cur = null;
            render();
          }
          if (k === "hd_kb" && d) {
            flushType();
            items.push({ kind: "hd_kb", hits: Array.isArray((d as any).hits) ? (d as any).hits : [], query: typeof (d as any).query === "string" ? (d as any).query : undefined, open: null, edit: null });
            cur = null;
            render();
          }
          if (k === "confirm" && d) {
            flushType();
            items.push({ kind: "confirm", connectorId: Number((d as any).connector_id), action: String((d as any).action || ""), summary: String((d as any).summary || ""), preview: ((d as any).preview || {}) as Record<string, unknown>, token: String((d as any).token || ""), state: "pending" });
            cur = null;
            render();
          }
        } else if (ev[1] === "notice") {
          // ‼️ Same handling as the pane: a capped turn is not something the user can fix by
          // rephrasing. Finish it in the background (~15 min, no wall) and say so in their
          // language; whatever already streamed stays on screen.
          // currentTurn is already maintained for "Mail me when ready" and covers every mode —
          // reusing it means the handoff can never drift from what that button sends.
          if (data.capped && currentTurn) {
            items.push({ kind: "notice", text: T.tooLong });
            const { messages: tm, ...topts } = currentTurn as any;
            void mailChatAsync(tm, topts).catch(() => {
              items.push({ kind: "notice", text: T.tooLong });
              render();
            });
          } else {
            items.push({ kind: "notice", text: String(data.text || "") });
          }
          render();
        }
        else if (ev[1] === "error") {
          // ‼️ A timeout arrives here as an error EVENT, not as a thrown abort — the same branch
          // that was missed in the pane. Treat it like a capped turn: finish in the background,
          // say so in the user's language, keep what already streamed.
          // ‼️ Same rule as the pane: decide on the STATE of the turn, not the wording of the
          // error. Safari says "Load failed", the server says "aborted due to timeout", Chrome
          // says something else again — but if the model had already started answering and the
          // stream then died, the turn did not finish, and that is what the background worker is
          // for. An auth or connector failure arrives BEFORE any text and still reads as an error.
          const msg = String(data.message || "error");
          const started = (() => {
            for (let i = items.length - 1; i >= turnStart; i--) {
              const x = items[i];
              if (x.kind === "user") return false;
              if (x.kind === "assistant" && x.text.trim()) return true;
            }
            return false;
          })();
          if ((started || /aborted due to timeout|timed? ?out/i.test(msg)) && currentTurn) {
            items.push({ kind: "notice", text: T.tooLong });
            const { messages: tm, ...topts } = currentTurn as any;
            void mailChatAsync(tm, topts).catch(() => { /* the notice already told the user */ });
          } else {
            items.push({ kind: "error", text: msg, again: { q, ...(effPin ? { mode: effPin } : {}), ...(opts?.sourceRetry ? { sourceRetry: true } : {}) } });
          }
          render();
        }
      }
      // done arrived → settle NOW instead of waiting for the socket to close (scenario 11).
      if (doneSeen) { try { void reader.cancel(); } catch { /* released */ } break; }
    }
    cleanupWatch();
    flushType();
    if (pendingChoices) { items.push({ kind: "choices", ...pendingChoices }); pendingChoices = null; cur = null; }
    // ‼️ EOF without done = a TRUNCATED stream (platform kill, dropped connection) — the old
    // "any close is success" semantics silently approved half answers.
    if (!doneSeen) {
      items.push({ kind: "error", text: "The connection dropped before the answer finished — it may be incomplete. Please try again.", again: { q, ...(effPin ? { mode: effPin } : {}), ...(opts?.sourceRetry ? { sourceRetry: true } : {}) } });
      reportTerminal("eof_without_done");
    } else {
      reportTerminal("done");
    }
    plopDone();
    settle();
  } catch (e) {
    cleanupWatch();
    flushType();
    if (mailHandoff) { mailHandoff = false; abortCtrl = null; busy = false; expertRunningFrom = null; thinkingStop(); render(); return; } // mail hand-off — no "Stopped" notice
    if ((e as Error)?.name === "AbortError") {
      if (abortReason === "watchdog") {
        items.push({ kind: "error", text: "No response from the server — the request was stopped. Please try again.", again: { q, ...(effPin ? { mode: effPin } : {}), ...(opts?.sourceRetry ? { sourceRetry: true } : {}) } });
        reportTerminal("watchdog");
      } else if (abortReason === "stale") {
        items.push({ kind: "error", text: "This request went stale while the app was in the background. Please ask again." });
        reportTerminal("visibility_stale");
      } else {
        items.push({ kind: "notice", text: pendingVoice ? "Interrupted." : "Stopped." });
        reportTerminal("user_abort");
      }
    } else {
      items.push({ kind: "error", text: String((e as Error)?.message || e), again: { q, ...(effPin ? { mode: effPin } : {}), ...(opts?.sourceRetry ? { sourceRetry: true } : {}) } });
      reportTerminal(typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "server_error");
    }
    settle();
  }
}

/** Fire-and-forget: run a Chat · CRM · module-mode turn on the engine and email the result to the paired user's mailbox. */
async function mailChatAsync(messages: { role: string; content: string }[], extra?: { mode?: BrainMode; connectorIds?: number[]; askExpert?: boolean; refine?: string; deepThinking?: boolean; auto?: boolean; prevRouted?: string | null; sourceRetry?: boolean }): Promise<void> {
  // Use the CAPTURED deepThinking when provided (mid-turn "Mail me when ready" re-runs the turn
  // with the value it had at send time), else fall back to the live global.
  const deep = extra?.deepThinking ?? deepThinking;
  // M1.1: an Auto turn handed to the background is ROUTED server-side before the worker starts —
  // without these flags a mailed answer came from open chat + web (the Audi Q7 mail).
  const autoFlags = extra?.auto
    ? { auto: true, autoModes: [...contentModes().map((c) => c.id), "faq"], ...(extra.prevRouted ? { prevRouted: extra.prevRouted } : {}) }
    : {};
  const retry = extra?.sourceRetry ? { sourceRetry: true } : {};
  const r = await fetch("/api/connect/chat-async", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify(extra?.mode === "crm" || extra?.mode === "vault"
      ? { messages, mode: extra.mode, connectorIds: extra.connectorIds, askExpert: extra.askExpert, refine: extra.refine, ...retry }
      : extra?.mode && isModuleMode(extra.mode)
        ? { messages, mode: extra.mode, deepThinking: deep, ...retry }
        : { messages, deepThinking: deep, ...autoFlags, ...retry }),
  });
  if (!r.ok) { const d = (await r.json().catch(() => null)) as { error?: string } | null; throw new Error(d?.error || `HTTP ${r.status}`); }
}

/** Mid-turn "Mail me when ready": stop the on-screen stream + re-run this turn in the background. */
function mailWhenReady(): void {
  if (!currentTurn) return;
  mailHandoff = true;
  abortCtrl?.abort();
  items.push({ kind: "notice", text: MAIL_QUEUED });
  render();
  const t = currentTurn;
  const p = t.mode === "crm" || t.mode === "vault"
    ? mailChatAsync(t.messages, { mode: t.mode, connectorIds: t.connectorIds, askExpert: t.askExpert, refine: t.refine })
    : t.mode && isModuleMode(t.mode)
      ? mailChatAsync(t.messages, { mode: t.mode, deepThinking: t.deepThinking })
      : mailChatAsync(t.messages);
  void p.catch((e) => { items.push({ kind: "error", text: String((e as Error)?.message || e) }); render(); });
}

/** Per-answer Ask Expert: run the deep pass for the answer at `idx` with the (optional) inline steer. */
function runExpert(idx: number): void {
  const sq = sourceQuestionForIndex(idx);
  if (!sq || busy) return;
  const steer = (($("#cnSteerIn") as HTMLInputElement | null)?.value || expertSteer).trim();
  expertOpenIdx = null;
  expertSteer = "";
  expertRunningFrom = idx;
  void send(sq, { askExpert: true, refine: steer || undefined });
}

// ── answer actions (same endpoints as desktop, via the proxy) ──
// Per-answer copy: a hover-revealed action under each assistant bubble (always visible on touch).
const CN_COPY_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
const CN_CHECK_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
function copyBtnHtml(idx: number): string {
  return `<button class="cn-copy" data-copy="${idx}" title="Copy answer" aria-label="Copy answer">${CN_COPY_ICON}</button>`;
}
// Ask Expert (spec §2.0) — the PRIMARY v1 affordance, ported to the phone. A per-CRM-answer button that
// re-runs the source question through the deep GLM-5.2 analyst pass (ranked, cited triage). English-only.
const CN_EXPERT_ICON = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v1.5M12 19.5V21M4.2 4.2l1.1 1.1M18.7 18.7l1.1 1.1M3 12h1.5M19.5 12H21M4.2 19.8l1.1-1.1M18.7 5.3l1.1-1.1"/><circle cx="12" cy="12" r="3.5"/></svg>';
function askExpertBtnHtml(idx: number): string {
  return `<button class="cn-expert" data-ask-expert="${idx}" title="Re-run this with the deep analyst pass — ranked, cited, slower (uses more of your allowance)" aria-label="Ask Expert">${CN_EXPERT_ICON}<span class="cn-expert-lb">Ask Expert</span></button>`;
}
// The question that produced the answer at `idx` = the nearest preceding user message.
function sourceQuestionForIndex(idx: number): string {
  for (let j = idx - 1; j >= 0; j--) { const it = items[j]; if (it.kind === "user") return it.text; }
  return "";
}
// Inject the copy-action styling from here (the shared app.css is owned elsewhere) — runs once.
function ensureCopyStyle(): void {
  if (document.getElementById("cn-copy-style")) return;
  const st = document.createElement("style");
  st.id = "cn-copy-style";
  st.textContent = `
.cn-ai { align-self: flex-start; max-width: 86%; display: flex; flex-direction: column; gap: 1px; }
.cn-ai .cn-msg.ai { max-width: 100%; }
.cn-copy { display: inline-flex; align-items: center; justify-content: center; padding: 5px; border: 1px solid transparent; border-radius: 999px; background: transparent; color: var(--text-mid); cursor: pointer; opacity: 0.55; transition: opacity .14s, color .14s, background .14s, border-color .14s; -webkit-tap-highlight-color: transparent; }
.cn-copy:active { opacity: 1; }
.cn-copy.done { opacity: 1; color: var(--ok); }
.cn-copy svg { flex-shrink: 0; }
/* ONE action row per answer: Copy (icon, first) · Ask Expert · Mail · Excel — wraps on the phone. */
.cn-acts-row { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 3px; }
/* Ask Expert — SAME neutral pill as the Mail/Excel actions (.cn-act); the ✦ icon is its distinguisher. */
.cn-expert { display: inline-flex; align-items: center; gap: 5px; border: 1px solid var(--border-strong); background: var(--bg-raised); color: var(--text-hi); border-radius: 999px; padding: 6px 13px; font-family: var(--font); font-size: 12px; line-height: 1; cursor: pointer; -webkit-tap-highlight-color: transparent; }
.cn-expert:active { opacity: 0.7; }
.cn-expert svg { flex-shrink: 0; }
/* Inline Ask Expert steer box (opens at the answer; the steer changes per question). */
.cn-acts-row.cn-steer { flex-wrap: nowrap; width: 100%; }
.cn-steer-in { flex: 1; min-width: 0; border: 1px solid var(--border-strong); background: var(--bg-raised); color: var(--text-hi); border-radius: 999px; padding: 7px 12px; font-family: var(--font); font-size: 12px; }
.cn-steer-in::placeholder { color: var(--text-mid); }
.cn-steer-in:focus { outline: none; border-color: var(--accent-deep); }
.cn-steer-go { white-space: nowrap; font-weight: 600; }
.cn-steer-x { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: none; border-radius: 999px; background: transparent; color: var(--text-mid); cursor: pointer; }
/* Running state at the Ask Expert spot while the deep pass is in flight. */
.cn-expert-run { gap: 10px; }
.cn-run-lb { display: inline-flex; align-items: center; gap: 7px; font-size: 12px; color: var(--text-mid); }
.cn-run-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--ok); animation: cnRunPulse 1s ease-in-out infinite; }
@keyframes cnRunPulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }`;
  document.head.appendChild(st);
}
// Copy the answer as clean plain text — strip **bold** markers, keep bullets/numbers so it pastes
// tidily. WKWebView often blocks the async clipboard API → execCommand fallback. Flashes the
// clicked button directly (no full re-render) so only that bubble shows the ✓.
async function copyAnswerText(text: string, btn: HTMLElement): Promise<void> {
  const plain = text.replace(/\*\*([^*]+)\*\*/g, "$1");
  let ok = false;
  if (navigator.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(plain); ok = true; } catch { /* fall through */ }
  }
  if (!ok) {
    try {
      const ta = document.createElement("textarea");
      ta.value = plain; ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;left:-9999px;opacity:0";
      document.body.appendChild(ta); ta.select();
      ok = document.execCommand("copy");
      document.body.removeChild(ta);
    } catch { ok = false; }
  }
  if (!ok) return;
  btn.classList.add("done");
  btn.innerHTML = CN_CHECK_ICON;
  setTimeout(() => { btn.classList.remove("done"); btn.innerHTML = CN_COPY_ICON; }, 1500);
}

async function mailMe(): Promise<void> {
  const ans = lastCompleteAnswer();
  const lastQ = [...items].reverse().find((i): i is Extract<Item, { kind: "user" }> => i.kind === "user");
  if (!ans) return;
  mailState = "sending";
  render();
  try {
    const r = await fetch("/api/connect/mail", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ subject: (lastQ?.text || "Your Connect answer").slice(0, 120), text: ans.text, ...(lastC360 ? { c360: lastC360 } : {}) }),
    });
    if (!r.ok) throw new Error("send_failed");
    mailState = "sent";
  } catch { mailState = "error"; }
  render();
}

async function downloadXls(): Promise<void> {
  const ans = lastCompleteAnswer();
  if (!ans || xlsState === "building") return;
  xlsState = "building";
  render();
  try {
    const r = await fetch("/api/connect/mail", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ text: ans.text, deliver: "download" }),
    });
    const j = (await r.json().catch(() => null)) as { filename?: string; xlsx_base64?: string; error?: string } | null;
    if (!r.ok) { xlsState = j?.error === "no_table" ? "none" : "error"; render(); return; }
    const bin = atob(String(j?.xlsx_base64 || ""));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = url; a.download = j?.filename || "rplay-connect.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    xlsState = "done";
  } catch { xlsState = "error"; }
  render();
}

// ── tap-dictate (companion voice: /api/dictate, transcript fires as a turn) ──
let dictMedia: MediaRecorder | null = null;
let dictStream: MediaStream | null = null;
let dictChunks: BlobPart[] = [];
let dictFmt = "webm";
function pickMime(): { mime: string; f: string } {
  const cands = [
    { mime: "audio/webm;codecs=opus", f: "webm" },
    { mime: "audio/webm", f: "webm" },
    { mime: "audio/mp4", f: "mp4" },
    { mime: "audio/ogg;codecs=opus", f: "ogg" },
  ];
  for (const c of cands) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c.mime)) return c;
  return { mime: "", f: "webm" };
}
function setDict(s: "idle" | "rec" | "busy"): void {
  $("#cnMic")?.setAttribute("data-dict", s);
}
async function toggleDict(): Promise<void> {
  const s = $("#cnMic")?.getAttribute("data-dict") || "idle";
  if (s === "busy") return;
  if (s === "rec") { try { dictMedia?.stop(); } catch { /* already stopped */ } return; }
  try { dictStream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch { toast("We couldn't access your microphone"); return; }
  const { mime, f } = pickMime();
  dictFmt = f; dictChunks = [];
  dictMedia = mime ? new MediaRecorder(dictStream, { mimeType: mime }) : new MediaRecorder(dictStream);
  dictMedia.ondataavailable = (ev) => { if (ev.data?.size) dictChunks.push(ev.data); };
  dictMedia.onstop = () => void dictFinish();
  dictMedia.start();
  setDict("rec");
}
async function dictFinish(): Promise<void> {
  const blob = new Blob(dictChunks);
  dictChunks = [];
  dictStream?.getTracks().forEach((t) => t.stop());
  dictStream = null; dictMedia = null;
  if (blob.size < 1200) { setDict("idle"); return; }
  setDict("busy");
  try {
    const b64 = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => { const v = String(fr.result || ""); res(v.slice(v.indexOf(",") + 1)); };
      fr.onerror = () => rej(new Error("read_failed"));
      fr.readAsDataURL(blob);
    });
    const r = await fetch("/api/dictate", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ audio_b64: b64, audio_format: dictFmt }),
    });
    const j = (await r.json().catch(() => ({}))) as { text?: string; error?: string };
    if (j.text) {
      // Conversational: the spoken question fires straight away; if an answer is still
      // streaming, queue it and barge in (same semantics as the desktop converse).
      if (busy) { pendingVoice = j.text; abortCtrl?.abort(); }
      else void send(j.text);
    } else if (j.error === "no_speech") toast("We couldn't hear any speech — try again");
    else toast("Dictation failed — try again");
  } catch { toast("Dictation failed — check your connection"); }
  finally { setDict("idle"); }
}

// ── tile gate + boot ──
function applyMeta(m: Meta): void {
  meta = m;
  // The Brain affordances (mode pills + mic/send) live in the Home launch bar, org-gated.
  const input = $("#hsInput") as HTMLInputElement | null;
  if (input) {
    input.placeholder = m.enabled ? "Search — or ask Brain anything…" : "Just search…";
  }
  // tell the Home launch bar to (re)render its pills + show/hide the mic+send now the gate is known
  document.dispatchEvent(new CustomEvent("rp:connect-meta", { detail: m.enabled }));
  // No org name in the Brain header (house canon: just "Brain · read-only").
}

/** Home launch bar → open the Brain chat in the picked mode (Chat by default); a typed
 *  question fires straight away (plug with an empty box just opens Brain in that mode). */
/** Does this user have a CRM connector (non-knowledge/sendcloud/helpdesk)? Gates the C360 CRM lane. */
export function hasCrmConnector(): boolean {
  return !!meta?.connectors.some((c) => c.type !== "knowledge" && c.type !== "sendcloud" && c.type !== "helpdesk");
}

export function askConnect(text: string, m?: BrainMode | "auto", opts?: { voice?: boolean; deepThinking?: boolean; mailMe?: boolean; askExpert?: boolean }): void {
  // "auto" (the Home launch bar's default since 2026-08-19) = open the chat under the Auto
  // policy; "chat" = explicit General & web; any other source switches the policy off too.
  if (m === "auto") webExplicit = false;
  else if (m) webExplicit = m === "chat";
  const target: BrainMode | undefined = m === "auto" ? "chat" : m;
  if (target && target !== mode) { items = []; mailState = "idle"; xlsState = "idle"; lastC360 = null; prevRouted = null; mode = target; } // fresh thread → no routed topic (review 2026-08-18)
  // Pre-arm from the Home launch bar's ⚡: the options chosen on the bar ride into the chat so the
  // first question runs with exactly them on. The launch bar is authoritative for this first turn —
  // an UNARMED launch resets the globals off (matches the desktop/pane).
  deepThinking = !!opts?.deepThinking;
  mailMeOn = !!opts?.mailMe;
  askExpertArmed = !!opts?.askExpert;
  recipesOpen = false;
  showScreen("connect");
  void loadConnect();
  if (opts?.voice) { render(); void toggleDict(); return; } // mic handoff: open + start dictation
  const q = text.trim();
  if (!q) { render(); return; }
  if (busy) { pendingVoice = q; abortCtrl?.abort(); } // barge in, like dictation
  else void send(q);
}

// The Home launch bar's ⚡ popover reads the SAME starters as the chat (module/faq = static; CRM = connectors).
export function startersForMode(m: BrainMode): Recipe[] {
  if (m === "crm") return meta ? meta.connectors.flatMap((c) => c.recipes) : [];
  return MODULE_RECIPES[m] || [];
}

// Stale-while-revalidate: a snapshotted gate paints the screen instantly; the fetch
// refreshes connectors/recipes in the background (and re-renders when they change).
// Revalidate the Quotes entitlement so the Quotes pill matches the Quotes tile (org-gated).
// 200 = enabled · 403 = off · 401 = not linked yet (retry on a later open). One probe per session.
let quotesProbed = false;
async function probeQuotesOn(): Promise<void> {
  if (quotesProbed) return;
  try {
    const r = await fetch("/api/quotes", { credentials: "include" });
    if (r.status === 401) return; // phone not linked yet — leave the cached gate, retry next open
    quotesProbed = true;
    const on = r.ok;
    saveSnap("quotes-on", on);
    if (on !== quotesOn) { quotesOn = on; renderModes(); }
  } catch { /* transient — keep the cached gate, retry next open */ }
}

export async function loadConnect(force = false): Promise<void> {
  void probeQuotesOn();
  // M1.1: the Auto empty-state cards lead with the user's saved recipes — warm them here (was
  // ⚡-open only) and repaint when they land late. One cached fetch; no polling.
  void fetchUserRecipes().then(() => { if (!items.length) render(); });
  if (!meta) {
    const snap = loadSnap<Meta>("connect-meta");
    if (snap?.enabled) applyMeta(snap);
  }
  if (meta) {
    render();
    void loadHealth(); // connector dots, after the screen is painted
    if (metaFresh && !force) return; // fresh this session — nothing to revalidate
  }
  try {
    const r = await fetch("/api/connect", { credentials: "include" });
    if (!r.ok) { if (!meta) meta = { enabled: false, connectors: [] }; return; } // keep the painted screen on auth/transport trouble
    applyMeta((await r.json()) as Meta);
    metaFresh = true;
    saveSnap("connect-meta", meta);
  } catch {
    if (!meta) meta = { enabled: false, connectors: [] };
    return;
  }
  render();
  void loadHealth();
}

/** Boot probe: unhide the home tile when the paired user's org has the module.
 *  The snapshot unhides it instantly (no pop-in); the fetch revalidates the gate. */
export async function probeConnectTile(): Promise<void> {
  const snap = loadSnap<Meta>("connect-meta");
  if (snap?.enabled) applyMeta(snap);
  try {
    const r = await fetch("/api/connect", { credentials: "include" });
    if (!r.ok) return; // auth/transport trouble — keep the cached gate
    applyMeta((await r.json()) as Meta);
    metaFresh = true;
    saveSnap("connect-meta", meta);
  } catch { /* tile keeps its cached state */ }
}

export function initConnect(): void {
  $("#cnSend")?.addEventListener("click", () => {
    if (busy) { abortCtrl?.abort(); return; }
    const input = $("#cnInput") as HTMLTextAreaElement | null;
    if (input) void send(input.value);
  });
  // Enter = a NEW LINE (Gery): sending is exclusively the green button. (The old
  // Enter-submits + enterkeyhint="send" double-signalled send on the iOS keyboard.)
  ($("#cnInput") as HTMLTextAreaElement | null)?.addEventListener("input", syncSend);
  $("#cnMic")?.addEventListener("click", () => void toggleDict());
  $("#cnClear")?.addEventListener("click", () => { const c = $("#cnConfirm"); if (c) c.style.display = ""; });
  $("#cnConfirmNo")?.addEventListener("click", () => { const c = $("#cnConfirm"); if (c) c.style.display = "none"; });
  $("#cnConfirmYes")?.addEventListener("click", () => {
    const c = $("#cnConfirm");
    if (c) c.style.display = "none";
    abortCtrl?.abort();
    abortCtrl = null;
    pendingVoice = null;
    busy = false;
    thinkingStop();
    items = [];
    mailState = "idle";
    xlsState = "idle";
    deepThinking = false;
    mailMeOn = false;
    currentTurn = null;
    mailHandoff = false;
    prevRouted = null; // new chat → no routed topic yet (mirrors ConnectView.clearChat; review 2026-08-18)
    render();
  });
  $("#cnThread")?.addEventListener("scroll", trackThreadScroll, { passive: true });
  // thread delegation: samples, recipes, chips, answer actions
  $("#cnThread")?.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const balloon = t.closest<HTMLElement>(".brain-mode[data-mode]"); // empty-state mode balloons
    if (balloon) { pickMode(balloon.getAttribute("data-mode") as BrainMode); return; }
    // M1 empty-state card: pre-picks its source for the first turn (requested_scope). An
    // open-ended starter ("Give me the dossier of ") fills the input instead of firing.
    const cardBtn = t.closest<HTMLElement>("[data-card-prompt]");
    if (cardBtn) {
      const prompt = cardBtn.getAttribute("data-card-prompt") || "";
      const pin = cardBtn.getAttribute("data-card-mode") || "";
      if (/\s$/.test(prompt)) {
        pickSource(pin);
        const inp = $("#cnInput") as HTMLTextAreaElement | null;
        if (inp) { inp.value = prompt; inp.focus(); }
      } else void send(prompt, pin ? { pinMode: pin } : undefined);
      return;
    }
    // M1.1 "Try another source": toggle the picker; a pick appends a re-run in that source
    // (sourceRetry forces a real lookup; "chat" logs the correction) — never erases.
    const ag = t.closest<HTMLElement>("[data-again]");
    if (ag) {
      const it = items[Number(ag.getAttribute("data-again"))];
      if (it?.kind === "error" && it.again && !it.againFired && !busy) {
        it.againFired = true; // one tap per failure; a second drop pushes its own line
        render();
        void send(it.again.q, {
          replayUser: true,
          ...(it.again.mode ? { pinMode: it.again.mode } : {}),
          ...(it.again.sourceRetry ? { sourceRetry: true } : {}),
        });
      }
      return;
    }
    const sr = t.closest<HTMLElement>("[data-srcretry]");
    if (sr) {
      const it = items[Number(sr.getAttribute("data-srcretry"))];
      if (it?.kind === "srcsum") { it.retryOpen = !it.retryOpen; render(); }
      return;
    }
    const sp = t.closest<HTMLElement>("[data-srcpick]");
    if (sp) {
      const [i2, target] = String(sp.getAttribute("data-srcpick")).split(":");
      const it = items[Number(i2)];
      if (it?.kind === "srcsum" && !busy && !it.retried && target) {
        it.retryOpen = false;
        it.retried = true;
        items.push({ kind: "retried", mode: target });
        render();
        void send(it.q, target === "chat"
          ? { pinMode: "chat", correctedFrom: it.route, replayUser: true }
          : { pinMode: target, replayUser: true, sourceRetry: true });
      }
      return;
    }
    // M1 routed-chip ✕: append a General & web re-run (never erase) + log the correction.
    const rx = t.closest<HTMLElement>("[data-routedx]");
    if (rx) {
      const it = items[Number(rx.getAttribute("data-routedx"))];
      if (it?.kind === "routed" && !busy && !it.corrected) {
        it.corrected = true;
        prevRouted = null;
        items.push({ kind: "retried", mode: "chat" });
        render();
        // ‼️ pinMode "chat" — the conversation is kept across source switches, so `mode` may be
        // anything at tap time; without the pin the re-run ran in that mode and correctedFrom
        // was dropped (review 2026-08-18, mirrors ConnectView.correctRoute).
        void send(it.q, { pinMode: "chat", correctedFrom: it.mode, replayUser: true });
      }
      return;
    }
    const kbOpen = t.closest<HTMLElement>("[data-kb-open]");
    if (kbOpen) { const [i2, k2] = String(kbOpen.getAttribute("data-kb-open")).split(":").map(Number); const it = items[i2]; if (it?.kind === "hd_kb") { it.open = it.open === k2 ? null : k2; if (it.edit === k2) it.edit = null; render(); } return; }
    const kbEdit = t.closest<HTMLElement>("[data-kb-edit]");
    if (kbEdit) { const [i2, k2] = String(kbEdit.getAttribute("data-kb-edit")).split(":").map(Number); const it = items[i2]; if (it?.kind === "hd_kb") { it.edit = k2; it.open = k2; it.err = undefined; render(); } return; }
    const hdChip = t.closest<HTMLElement>("[data-hd-state]");
    if (hdChip) { const st = hdChip.getAttribute("data-hd-state") === "closed" ? "closed" as const : "open" as const; if (st !== hdState) { hdState = st; void loadHdSnap(); render(); } return; }
    const kbAdd = t.closest<HTMLElement>("[data-kb-add]");
    if (kbAdd) { const it = items[Number(kbAdd.getAttribute("data-kb-add"))]; if (it?.kind === "hd_kb") { it.adding = true; it.err = undefined; render(); } return; }
    const kbAddSave = t.closest<HTMLElement>("[data-kb-addsave]");
    if (kbAddSave) { void addKbEntry(Number(kbAddSave.getAttribute("data-kb-addsave"))); return; }
    const kbAddCancel = t.closest<HTMLElement>("[data-kb-addcancel]");
    if (kbAddCancel) { const it = items[Number(kbAddCancel.getAttribute("data-kb-addcancel"))]; if (it?.kind === "hd_kb") { it.adding = false; render(); } return; }
    const kbSave = t.closest<HTMLElement>("[data-kb-save]");
    if (kbSave) { const [i2, k2] = String(kbSave.getAttribute("data-kb-save")).split(":").map(Number); void saveKbEdit(i2, k2); return; }
    const kbCancel = t.closest<HTMLElement>("[data-kb-cancel]");
    if (kbCancel) { const it = items[Number(kbCancel.getAttribute("data-kb-cancel"))]; if (it?.kind === "hd_kb") { it.edit = null; render(); } return; }
    const cfEdit = t.closest<HTMLElement>("[data-cf-editmode]");
    if (cfEdit) { const it = items[Number(cfEdit.getAttribute("data-cf-editmode"))]; if (it?.kind === "confirm" && it.state === "pending") { it.editing = true; render(); } return; }
    const cfGoClose = t.closest<HTMLElement>("[data-cf-approve-close]");
    if (cfGoClose) { void approveConfirmItem(Number(cfGoClose.getAttribute("data-cf-approve-close")), true); return; }
    const cfGo = t.closest<HTMLElement>("[data-cf-approve]");
    if (cfGo) { void approveConfirmItem(Number(cfGo.getAttribute("data-cf-approve"))); return; }
    const cfX = t.closest<HTMLElement>("[data-cf-cancel]");
    if (cfX) { const it = items[Number(cfX.getAttribute("data-cf-cancel"))]; if (it?.kind === "confirm" && it.state === "pending") { it.state = "cancelled"; render(); } return; }
    // Meeting Scan picker + scan offer chips (checked BEFORE the generic sample branch — these
    // are API calls, not chat messages, so the subject can never be lost to routing).
    const scanPick = t.closest<HTMLElement>("[data-scan-pick]");
    if (scanPick) {
      const [i2, k2] = String(scanPick.getAttribute("data-scan-pick")).split(":").map(Number);
      const it = items[i2];
      if (it?.kind === "scanpick" && !it.picked) {
        const m = (it.meetings || [])[k2];
        if (m) { it.picked = m.title; render(); fireScan({ meeting: { ...m, date: it.date ?? null } }, m.time ? `${m.title} (${it.when} ${m.time})` : m.title); }
      }
      return;
    }
    const scanDay = t.closest<HTMLElement>("[data-scan-day]");
    if (scanDay) {
      const it = items[Number(scanDay.getAttribute("data-scan-day"))];
      if (it?.kind === "scanpick") openMeetingScan(it.when === "today" ? "tomorrow" : "today", it);
      return;
    }
    const scanAll = t.closest<HTMLElement>("[data-scan-all]");
    if (scanAll) {
      const it = items[Number(scanAll.getAttribute("data-scan-all"))];
      if (it?.kind === "scanpick" && !it.picked) { it.picked = "__all__"; render(); fireScan({ all: it.when }, `all of ${it.when} (${(it.meetings || []).length} meetings)`); }
      return;
    }
    const scanSub = t.closest<HTMLElement>("[data-scan-subject]");
    if (scanSub) { fireScan({ subject: scanSub.getAttribute("data-scan-subject") || "" }, scanSub.getAttribute("data-scan-subject") || ""); return; }
    const sample = t.closest<HTMLElement>(".cn-sample, .cn-recipe");
    if (sample) {
      // A handoff chip belongs to another mode (the archive lives in Vault) — askConnect switches
      // mode AND asks, the same path the 360 fiche uses.
      const hm = sample.getAttribute("data-handoff-mode");
      if (hm) { askConnect(sample.getAttribute("data-prompt") || "", hm as BrainMode); return; }
      const pin = sample.getAttribute("data-pin-mode");
      void send(sample.getAttribute("data-prompt") || "", pin ? { pinMode: pin } : undefined);
      return;
    }
    const copyBtn = t.closest<HTMLElement>(".cn-copy");
    if (copyBtn) {
      const it = items[Number(copyBtn.getAttribute("data-copy"))];
      if (it && it.kind === "assistant") void copyAnswerText(it.text, copyBtn);
      return;
    }
    // Ask Expert steer run/cancel + the per-answer "Mail me when ready" (check before .cn-expert — the
    // run button shares that class).
    const steerGo = t.closest<HTMLElement>("[data-steer-go]");
    if (steerGo) { runExpert(Number(steerGo.getAttribute("data-steer-go"))); return; }
    if (t.closest("[data-steer-x]")) { expertOpenIdx = null; expertSteer = ""; render(); return; }
    if (t.closest("[data-mailwhen]")) { mailWhenReady(); return; }
    const expertBtn = t.closest<HTMLElement>(".cn-expert[data-ask-expert]"); // the per-answer button → open the inline steer box
    if (expertBtn) {
      expertOpenIdx = Number(expertBtn.getAttribute("data-ask-expert"));
      expertSteer = "";
      render(); // the render focus-hook focuses #cnSteerIn
      return;
    }
    const thinkHead = t.closest<HTMLElement>(".cn-think-h"); // expand/collapse the Deep-thinking trace
    if (thinkHead) {
      const it = items[Number(thinkHead.getAttribute("data-think"))];
      if (it && it.kind === "thinking") { it.open = !it.open; render(); }
      return;
    }
    if (t.closest("#cnMailWhen")) { mailWhenReady(); return; }
    if (t.closest("#cnMailMe")) { void mailMe(); return; }
    if (t.closest("#cnXls")) { void downloadXls(); return; }
  });
  // Inline Ask Expert steer box: sync `expertSteer` WITHOUT re-rendering (keeps focus) + Enter/Escape.
  $("#cnThread")?.addEventListener("input", (e) => {
    const t = e.target as HTMLElement;
    if (t.id === "cnSteerIn") expertSteer = (t as HTMLInputElement).value;
  });
  $("#cnThread")?.addEventListener("keydown", (e) => {
    const t = e.target as HTMLElement;
    if (t.id !== "cnSteerIn" || expertOpenIdx === null) return;
    if (e.key === "Enter") { e.preventDefault(); runExpert(expertOpenIdx); }
    else if (e.key === "Escape") { expertOpenIdx = null; expertSteer = ""; render(); }
  });
  ($("#cnRecipeSearch") as HTMLInputElement | null)?.addEventListener("input", (e) => {
    recipesQ = (e.target as HTMLInputElement).value;
    renderRecipeList();
  });
  // outside tap closes the recipes popover (house dropdown behaviour)
  document.addEventListener("pointerdown", (e) => {
    if (!recipesOpen) return;
    const t = e.target as Element | null;
    if (t?.closest?.("#cnRecipes") || t?.closest?.(".brain-mode.zap")) return;
    recipesOpen = false;
    render();
  });
  $("#rcpScrim")?.addEventListener("click", () => void guardedCloseRecipe());
  $("#rcpClose")?.addEventListener("click", () => void guardedCloseRecipe());
  $("#rcpFocus")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-focus]");
    const v = b?.getAttribute("data-focus");
    if (v === "news" || v === "papers" || v === "both") { rcpFocus = v; rcpSyncFocus(); }
  });
  $("#rcpSave")?.addEventListener("click", () => void saveUserRecipe());
  $("#rcpDelete")?.addEventListener("click", () => void deleteUserRecipe());
  $("#cnRecipes")?.addEventListener("click", (e) => {
    const opt = (e.target as HTMLElement).closest<HTMLElement>(".chat-opt-row"); // Deep thinking / Mail me toggles
    if (opt) {
      const which = opt.getAttribute("data-opt");
      if (which === "scan-meetings") { openMeetingScan("today"); return; } // closes the popover itself
      if (which === "scan-deep") {
        recipesOpen = false;
        scanAsk = true;
        items.push({ kind: "notice", text: "📡 Deep Scan — type the company or contact below and press send." });
        render();
        ($("#cnInput") as HTMLTextAreaElement | null)?.focus();
        return;
      }
      if (which === "deep") deepThinking = !deepThinking;
      else if (which === "mail") mailMeOn = !mailMeOn;
      else if (which === "arm") askExpertArmed = !askExpertArmed;
      renderModes(); // refresh the popover toggles + the ⚡ lit state (keep the popover open)
      return;
    }
    const ed = (e.target as HTMLElement).closest<HTMLElement>("[data-redit]");
    if (ed) {
      const rec = userRecipes.find((x) => String(x.id) === ed.getAttribute("data-redit"));
      if (rec) { recipesOpen = false; renderModes(); openRecipeEditor(rec); }
      return;
    }
    if ((e.target as HTMLElement).closest<HTMLElement>("[data-rnew]")) { recipesOpen = false; renderModes(); openRecipeEditor(null); return; }
    const r = (e.target as HTMLElement).closest<HTMLElement>(".cn-recipe");
    if (r) {
      recipesOpen = false;
      // M1.1: a mixed suggestion carries its source — an open-ended prompt ("… about ") fills the
      // composer in that source instead of firing half a question.
      const pin = r.getAttribute("data-pin-mode");
      const prompt = r.getAttribute("data-prompt") || "";
      if (pin && /\s$/.test(prompt)) {
        pickSource(pin);
        const inp = $("#cnInput") as HTMLTextAreaElement | null;
        if (inp) { inp.value = prompt; inp.focus(); }
        return;
      }
      void send(prompt, pin ? { pinMode: pin } : undefined);
    }
  });
  // M1 bar: the Auto ▾ selector picks the source (conversation KEPT); ⚡ toggles the starters popover
  $("#cnChips")?.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const so = t.closest<HTMLElement>("[data-selopt]");
    if (so) { pickSource(so.getAttribute("data-selopt") || "auto"); return; }
    if (t.closest<HTMLElement>("[data-seltoggle]")) { selOpen = !selOpen; render(); return; }
    const btn = t.closest<HTMLElement>(".brain-mode");
    if (!btn) return;
    if (btn.hasAttribute("data-zap")) { recipesOpen = !recipesOpen; render(); return; }
    const m = btn.getAttribute("data-mode") as BrainMode | null;
    if (m) pickMode(m);
  });
  // The selector popover closes on outside tap or Escape (house dropdown behaviour).
  // ‼️ On CLICK, not pointerdown: this closer re-renders the whole thread+bar via innerHTML, and
  // a pointerdown re-render destroys the element under the finger BEFORE its click can fire —
  // every first tap on ⚡/cards/thread controls was eaten while the popover was open
  // (review 2026-08-18; React's reconciliation shields the pane from this, innerHTML has no such shield).
  document.addEventListener("click", (e) => {
    if (selOpen && !(e.target as Element | null)?.closest?.(".brain-selwrap")) { selOpen = false; render(); }
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && selOpen) { selOpen = false; render(); }
  });
  // MCP-Apps cards: ONE message listener serves every card; re-pin the hosts when the viewport
  // changes (rotation, keyboard) and when the Connect screen comes back — offsets measured
  // while the screen was display:none are meaningless, so measure again once it has layout.
  window.addEventListener("message", (ev) => void onMcpMessage(ev));
  window.addEventListener("resize", positionMcpCards);
  document.addEventListener("rp:screen", (e) => {
    if ((e as CustomEvent).detail === "connect") requestAnimationFrame(positionMcpCards);
  });
  if (import.meta.env.DEV) {
    // Dev-only harness (stripped from production builds): inject a card from the console
    // without a linked session — window.__rpTestMcpCard(html, { connector_id, ... }?).
    // Pair it with the vault's render_test_card output to exercise the full handshake.
    (window as unknown as Record<string, unknown>).__rpTestMcpCard = (html: string, payload?: Partial<McpApp>): void => {
      if (!meta) meta = { enabled: true, connectors: [] };
      showScreen("connect");
      items.push({ kind: "mcp_app", key: `m${++mcpSeq}`, app: { connector_id: 0, tool: "test", resource_uri: "", html, content: [], structuredContent: null, _meta: null, ...payload } });
      render();
    };
  }
  // Leaving the Connect screen or backgrounding the app silences the thinking loop
  // (the running turn finishes in the background and lands in the thread).
  document.addEventListener("rp:screen", (e) => {
    if ((e as CustomEvent).detail !== "connect") thinkingStop();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") thinkingStop();
  });
  void probeConnectTile();
}
